import { defineStore, storeToRefs } from 'pinia'
import { ref, watch, computed } from 'vue'
import { useAuthStore } from './authStore'
import { db } from '../firebase'
import {
  collection,
  doc,
  query,
  where,
  or,
  and,
  limit,
  type Unsubscribe
} from 'firebase/firestore'
import {
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  onSnapshot,
  writeBatch
} from '../services/firestoreTracked'
import { useNotifications } from '../composables/useNotifications'
import { parseAmountAndUnit } from '../utils/amountParser'
import { useSystemStore, type InAppNotification } from './systemStore'
import { useRecipeProgressStore, type SavedRecipeProgress } from './recipeProgressStore'

export type { InAppNotification, SavedRecipeProgress }
import { createEntityReplacementService } from '../services/entityReplacementService'
import { createRecurringPurchasesService } from '../services/recurringPurchasesService'
import { QUOTA_CONFIG } from '../constants/quotaConfig'
import { sanitizeTextInput, sanitizeUrl } from '../utils/sanitizer'
import { cleanArticleName } from '../utils/cleanArticleName'
import { safeLocalStorageGet, safeLocalStorageSet } from '../utils/storageHelper'
import {
  initialArticleCategories,
  initialArticles,
  initialRecipeCategories,
  initialRecipes,
  initialUnits,
  initialShoppingList,
  DEFAULT_RECIPE_TAGS,
  type ArticleCategory,
  type Article,
  type RecipeCategory,
  type Recipe,
  type RecipeIngredient,
  type ShoppingCartItem,
  type DeletedItem,
  type Unit,
  type RecurringPurchase
} from '../data/mockData'
import { extractDominantColor } from '../composables/useColorThief'
import { getImageUrl } from '../utils/imageUtils'

export const useAppStore = defineStore('app', () => {
  const systemStore = useSystemStore()
  const recipeProgressStore = useRecipeProgressStore()

  const {
    toasts,
    confirmDialogState,
    deleteWithReplacementState,
    isNotificationsModalOpen,
    notificationsHistory,
    themeMode,
    darkMode,
    compactTiles,
    quickAddToCart,
    autoDeleteCheckedItems12h,
    autoDeleteCheckedItems24h,
    startPage,
    mainSwipeMode,
    keepScreenAwake,
    keepScreenAwakeUseDuration,
    invertCategoryMultiSelect,
    speechVoiceURI,
    speechRate,
    speechPitch,
    supportUrl,
    pixabayApiKey,
    pexelsApiKey,
    unsplashAccessKey,
    unsplashAppId,
    unsplashSecret,
    settingsAvailableTabs,
    settingsTabTrigger
  } = storeToRefs(systemStore)

  const {
    showToast,
    dismissToast,
    confirmAction,
    handleConfirmResult,
    handleDeleteWithReplacementResult,
    addNotification,
    markNotificationRead,
    markAllNotificationsRead,
    clearNotificationHistory,
    deleteNotification,
    updateSupportUrl,
    updateImageApiKeys,
    triggerSettingsTabOffset
  } = systemStore

  const {
    isStepCompleted,
    setStepCompleted,
    getCompletedStepsCount,
    getCompletedStepsSet,
    isIngredientChecked,
    toggleIngredientChecked,
    resetRecipeProgress,
    hasRecipeProgress,
    initSync: initRecipeProgressSync,
    stopSync: stopRecipeProgressSync
  } = recipeProgressStore

  // Reactive State
  const articleCategories = ref<ArticleCategory[]>([])
  const articles = ref<Article[]>([])
  const recipeCategories = ref<RecipeCategory[]>([])
  const recipes = ref<Recipe[]>([])

  const getRecipesForBook = (bookId: string) => {
    if (!bookId) return recipes.value
    const currentBook = recipeBooks.value.find(b => b.id === bookId)
    if (!currentBook) return recipes.value

    return recipes.value.filter(r => {
      const bookIdsList = r.bookIds && r.bookIds.length > 0 ? r.bookIds : (r.bookId ? [r.bookId] : [])
      if (bookIdsList.length > 0) {
        return bookIdsList.includes(bookId)
      }
      const defaultOwnerBook = recipeBooks.value.find(b => b.ownerId === (r.ownerId || currentBook.ownerId))
      const defaultOwnerBookId = defaultOwnerBook ? defaultOwnerBook.id : ('book-' + (r.ownerId || currentBook.ownerId))
      return bookId === defaultOwnerBookId
    })
  }

  const recipesForActiveBook = computed(() => getRecipesForBook(activeBookId.value))

  // Gather unique tags across default presets and all available recipes
  const allRecipeTags = computed<string[]>(() => {
    const tagMap = new Map<string, string>()

    // 1. Initial default presets (Vegan, Vegetarisch, Laktosefrei, etc.)
    for (const tag of DEFAULT_RECIPE_TAGS) {
      const trimmed = tag.trim()
      if (trimmed) {
        tagMap.set(trimmed.toLowerCase(), trimmed)
      }
    }

    // 2. User-created tags from recipes
    for (const recipe of recipes.value) {
      if (Array.isArray(recipe.tags)) {
        for (const tag of recipe.tags) {
          const trimmed = (tag || '').trim()
          if (trimmed) {
            const lower = trimmed.toLowerCase()
            tagMap.set(lower, trimmed)
          }
        }
      }
    }
    return Array.from(tagMap.values()).sort((a, b) => a.localeCompare(b, 'de', { sensitivity: 'base' }))
  })
  const shoppingList = ref<ShoppingCartItem[]>([])
  const isShoppingListSyncing = ref<boolean>(false)
  const shoppingListByListIdCache = new Map<string, ShoppingCartItem[]>()

  const getCachedShoppingList = (listId: string): ShoppingCartItem[] | undefined => {
    if (shoppingListByListIdCache.has(listId)) return shoppingListByListIdCache.get(listId)
    const authStore = useAuthStore()
    const uid = authStore.user?.uid
    if (uid) {
      if (listId === uid && shoppingListByListIdCache.has('shop-list-default-' + uid)) {
        return shoppingListByListIdCache.get('shop-list-default-' + uid)
      }
      if (listId === ('shop-list-default-' + uid) && shoppingListByListIdCache.has(uid)) {
        return shoppingListByListIdCache.get(uid)
      }
    }
    return undefined
  }

  const setCachedShoppingList = (listId: string, items: ShoppingCartItem[]) => {
    shoppingListByListIdCache.set(listId, items)
    const authStore = useAuthStore()
    const uid = authStore.user?.uid
    if (uid) {
      if (listId === uid) {
        shoppingListByListIdCache.set('shop-list-default-' + uid, items)
      } else if (listId === ('shop-list-default-' + uid)) {
        shoppingListByListIdCache.set(uid, items)
      }
    }
  }
  const trashBin = ref<DeletedItem[]>([])
  const units = ref<Unit[]>([])
  const activeHeaderColor = ref<string | null>(null)
  const recipeColors = ref<Record<string, string>>({})
  const activeRecipeIdOverride = ref<string | null>(null)
  const activeHeaderBgImage = ref<string | null>(null)
  const activeHeaderShowBg = ref<boolean>(false)
  const requestEditRecipe = ref<boolean>(false)
  const requestShareRecipe = ref<boolean>(false)
  const isGlobalSearchOpen = ref<boolean>(false)
  const openGlobalSearch = () => { isGlobalSearchOpen.value = true }
  const closeGlobalSearch = () => { isGlobalSearchOpen.value = false }
  const currentRecipeIdsList = ref<string[]>([])
  const resolvedRecipeIdsList = computed(() => {
    if (currentRecipeIdsList.value && currentRecipeIdsList.value.length > 0) {
      return currentRecipeIdsList.value
    }
    const favs = recipes.value.filter(r => r.favorite).sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }))
    const others = recipes.value.filter(r => !r.favorite).sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }))
    return [...favs, ...others].map(r => r.id)
  })
  const includeCheckedInCopy = ref<boolean>(false)
  const pendingFocusItemId = ref<string | null>(null)
  const activeEditingShoppingItemId = ref<string | null>(null)
  const lastInlineEditClosedAt = ref<number>(0)
  const suppressShoppingItemClickUntil = ref<number>(0)

  watch(activeEditingShoppingItemId, (newVal, oldVal) => {
    if (oldVal && !newVal) {
      lastInlineEditClosedAt.value = Date.now()
    }
  }, { flush: 'sync' })
  const selectedShoppingItemIds = ref<string[]>([])
  const editingArticleItemId = ref<string | null>(null)

  const toggleShoppingItemSelection = (id: string, forceSelect = false) => {
    const idx = selectedShoppingItemIds.value.indexOf(id)
    if (forceSelect) {
      if (idx === -1) {
        selectedShoppingItemIds.value.push(id)
      }
    } else {
      if (idx !== -1) {
        selectedShoppingItemIds.value.splice(idx, 1)
      } else {
        selectedShoppingItemIds.value.push(id)
      }
    }
  }

  const selectAllShoppingItems = (itemIds: string[]) => {
    selectedShoppingItemIds.value = Array.from(new Set([...selectedShoppingItemIds.value, ...itemIds]))
  }

  const clearShoppingItemSelection = () => {
    selectedShoppingItemIds.value = []
  }

  const openArticleEditForSelected = () => {
    if (selectedShoppingItemIds.value.length === 1) {
      const targetId = selectedShoppingItemIds.value[0]
      editingArticleItemId.value = targetId
      clearShoppingItemSelection()
    }
  }

  const shoppingListSlim = ref<boolean>(false)
  const shoppingListHideHeaders = ref<boolean>(false)
  const watchTargetListId = ref<string>('')
  const watchVibrationEnabled = ref<boolean>(true)
  const watchVoiceFeedbackEnabled = ref<boolean>(true)
  const watchAutoListenOnLaunch = ref<boolean>(false)
  const watchKeepScreenOnCooking = ref<boolean>(true)
  const watchKeepScreenOnShopping = ref<boolean>(true)
  const watchDimAmbientMode = ref<boolean>(true)
  const recurringPurchases = ref<RecurringPurchase[]>([])
  const desktopShoppingMode = ref<'planning' | 'shopping'>(
    (safeLocalStorageGet('shopping_desktop_mode', 'planning') as 'planning' | 'shopping') || 'planning'
  )
  watch(desktopShoppingMode, (newVal) => {
    safeLocalStorageSet('shopping_desktop_mode', newVal)
  }, { flush: 'sync' })

  // Helpers to read persistent IDs without demo pollution
  const getPersistedBookId = () => {
    const authStore = useAuthStore()
    const uid = authStore.user?.uid
    if (uid) {
      const userVal = safeLocalStorageGet(`activeBookId_${uid}`, '')
      if (userVal && userVal !== 'demo-book') return userVal
    }
    const val = safeLocalStorageGet('activeBookId', '')
    return (val && val !== 'demo-book') ? val : ''
  }
  const getPersistedListId = () => {
    const authStore = useAuthStore()
    const uid = authStore.user?.uid
    if (uid) {
      const userVal = safeLocalStorageGet(`activeListId_${uid}`, '')
      if (userVal && userVal !== 'demo-list') return userVal
    }
    const val = safeLocalStorageGet('activeListId', '')
    return (val && val !== 'demo-list') ? val : ''
  }

  // Shopping List Sharing State
  const shoppingLists = ref<any[]>([])
  const activeListId = ref<string>(getPersistedListId())
  const pendingInvitations = ref<any[]>([])
  const pendingRecipeInvitations = ref<any[]>([])
  const recipeBooks = ref<any[]>([])
  const activeBookId = ref<string>(getPersistedBookId())
  const pendingRecipeBookInvitations = ref<any[]>([])
  const listUncheckedCounts = ref<Record<string, number>>({})

  const totalPendingInvitationsCount = computed(() => {
    return (pendingInvitations.value?.length || 0) +
           (pendingRecipeBookInvitations.value?.length || 0) +
           (pendingRecipeInvitations.value?.length || 0)
  })

  const totalUncheckedShoppingCount = computed(() => {
    const listIds = shoppingLists.value.length > 0
      ? shoppingLists.value.map(l => l.id)
      : Object.keys(listUncheckedCounts.value)

    if (listIds.length === 0) {
      if (!shoppingList.value) return 0
      return shoppingList.value.filter(
        item => !item.checked && item.articleID !== '__LIST__' && item.articleID !== '__INVITATION__'
      ).length
    }

    const currentActiveListId = activeListId.value || getUid() || ''
    const currentActiveCount = (shoppingList.value || []).filter(
      item => !item.checked && item.articleID !== '__LIST__' && item.articleID !== '__INVITATION__'
    ).length

    let sum = 0
    let includedActive = false
    for (const id of listIds) {
      if (id === currentActiveListId) {
        sum += currentActiveCount
        includedActive = true
      } else {
        sum += (listUncheckedCounts.value[id] || 0)
      }
    }
    if (!includedActive && currentActiveCount > 0) {
      sum += currentActiveCount
    }
    return sum
  })

  // Synchronize PWA App Badge with uncompleted shopping items
  watch(totalUncheckedShoppingCount, (count) => {
    if (typeof navigator !== 'undefined' && 'setAppBadge' in navigator) {
      try {
        if (count > 0) {
          (navigator as any).setAppBadge(count).catch(() => {})
        } else if ('clearAppBadge' in navigator) {
          (navigator as any).clearAppBadge().catch(() => {})
        }
      } catch {}
    }
  }, { immediate: true })

  // Onboarding Dialog State (First login after registration)
  const isOnboardingOpen = ref(false)
  const isSettingUpOnboarding = ref(false)

  const unreadNotificationsCount = computed(() => {
    const pendingCount = (pendingInvitations.value?.length || 0) +
                         (pendingRecipeBookInvitations.value?.length || 0) +
                         (pendingRecipeInvitations.value?.length || 0)
    const unreadHistoryCount = notificationsHistory.value.filter(n => !n.read).length
    return pendingCount + unreadHistoryCount
  })

  // Wraps any Firebase write in try/catch and shows a user-friendly toast on failure.
  // Since Firestore offline persistence queues writes locally, real failures are mostly
  // auth/permission issues. When offline, writes are queued in IndexedDB without hanging the UI.
  const safeFirestore = async <T>(
    fn: () => Promise<T>,
    errorMessage = 'Fehler beim Speichern – bitte nochmal versuchen.',
    onError?: (err: any) => void
  ): Promise<T | null> => {
    if (isDemoMode.value) {
      return null
    }
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine
    if (isOffline) {
      try {
        const p = fn()
        if (p && typeof p.catch === 'function') {
          p.catch((err) => {
            console.warn('[Firestore Offline Queued]', err)
            onError?.(err)
          })
        }
      } catch (err) {
        console.warn('[Firestore Offline Error]', err)
        onError?.(err)
      }
      return null
    }

    try {
      if (import.meta.env?.TEST) {
        return await fn()
      }
      // When online, race with a 1500ms timeout so slow or dropping connections don't freeze the UI
      const promise = fn()
      let timer: any
      const timeoutPromise = new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), 1500)
      })
      const res = await Promise.race([promise, timeoutPromise])
      clearTimeout(timer)
      return res
    } catch (err: any) {
      onError?.(err)
      const code: string = err?.code || ''
      if (code === 'permission-denied' || code === 'unauthenticated') {
        showToast('Keine Berechtigung – bitte neu anmelden.', 'error')
      } else if (
        code === 'unavailable' ||
        code === 'network-request-failed' ||
        code === 'failed-precondition'
      ) {
        showToast('Keine Verbindung – Änderung wird synchronisiert sobald du wieder online bist.', 'error')
      } else {
        showToast(errorMessage, 'error')
      }
      return null
    }
  }

  


  let unsubscribes: Unsubscribe[] = []
  let countUnsubs = new Map<string, Unsubscribe>()
  let shoppingListUnsub: Unsubscribe | null = null
  let recurringPurchasesUnsub: Unsubscribe | null = null
  let currentSubscribedListId = ''

  const isDemoMode = computed(() => !useAuthStore().user && !useAuthStore().isRestoringSession)
  const hasInitialSyncCompleted = ref(false)

  const waitForInitialSync = (timeoutMs = 2500): Promise<void> => {
    if (hasInitialSyncCompleted.value || !useAuthStore().user) return Promise.resolve()
    return new Promise((resolve) => {
      let unwatch: (() => void) | null = null
      const timer = setTimeout(() => {
        if (unwatch) unwatch()
        resolve()
      }, timeoutMs)

      unwatch = watch(hasInitialSyncCompleted, (completed) => {
        if (completed) {
          clearTimeout(timer)
          if (unwatch) unwatch()
          resolve()
        }
      })
    })
  }

  // Clean up listeners when logging out
  const clearStore = () => {
    unsubscribes.forEach(unsub => unsub())
    unsubscribes = []
    
    articleCategories.value = []
    articles.value = []
    recipeCategories.value = []
    recipes.value = []
    shoppingList.value = []
    shoppingListByListIdCache.clear()
    isShoppingListSyncing.value = false
    trashBin.value = []
    units.value = []
    activeHeaderColor.value = null
    includeCheckedInCopy.value = false
    shoppingListSlim.value = false
    shoppingListHideHeaders.value = false
    watchTargetListId.value = ''
    watchVibrationEnabled.value = true
    watchVoiceFeedbackEnabled.value = true
    watchAutoListenOnLaunch.value = false
    watchKeepScreenOnCooking.value = true
    watchKeepScreenOnShopping.value = true
    watchDimAmbientMode.value = true

    shoppingLists.value = []
    activeListId.value = getPersistedListId()
    activeBookId.value = getPersistedBookId()
    pendingInvitations.value = []
    listUncheckedCounts.value = {}
    recurringPurchases.value = []
    countUnsubs.forEach(unsub => unsub())
    countUnsubs.clear()
    if (shoppingListUnsub) {
      shoppingListUnsub()
      shoppingListUnsub = null
    }
    if (recurringPurchasesUnsub) {
      recurringPurchasesUnsub()
      recurringPurchasesUnsub = null
    }
    locallyDeletedIds.value.clear()
    currentSubscribedListId = ''
    activeSyncUid = null
    recipeProgressStore.stopSync()
    isOnboardingOpen.value = false
    isSettingUpOnboarding.value = false
  }

  const loadDemoData = (force = false) => {
    if (!force && recipes.value.length > 0 && isDemoMode.value) {
      return
    }
    clearStore()

    // Load rich preset demo datasets without any Firestore subscription
    const clonedArticles = JSON.parse(JSON.stringify(initialArticles))
    articleCategories.value = JSON.parse(JSON.stringify(initialArticleCategories))
    articles.value = clonedArticles
    recipeCategories.value = JSON.parse(JSON.stringify(initialRecipeCategories))
    recipes.value = JSON.parse(JSON.stringify(initialRecipes))
    units.value = JSON.parse(JSON.stringify(initialUnits))
    shoppingList.value = JSON.parse(JSON.stringify(initialShoppingList)).map((item: any) => {
      if (!item.articleName && item.articleID) {
        const matched = clonedArticles.find((a: any) => a.id === item.articleID)
        if (matched) item.articleName = matched.name
      }
      return item
    })
    trashBin.value = []
    shoppingLists.value = [
      { id: 'demo-list', name: 'Standard-Liste', ownerId: 'demo-user', members: [] }
    ]
    recipeBooks.value = [
      { id: 'demo-book', name: 'Standard-Kochbuch', ownerId: 'demo-user', members: [] }
    ]
    activeListId.value = 'demo-list'
    activeBookId.value = 'demo-book'
    hasInitialSyncCompleted.value = true
  }

  let activeSyncUid: string | null = null
  let isInitializing = false

  const initialize = async () => {
    const authStore = useAuthStore()
    if (!authStore.isAuthReady) {
      try {
        await authStore.initializeAuth()
      } catch {}
    }
    const uid = authStore.user?.uid
    if (!uid) {
      loadDemoData()
      return
    }

    // Guard against duplicate sync for the same active session
    if (isInitializing || (activeSyncUid === uid && unsubscribes.length > 0)) {
      return
    }

    isInitializing = true
    try {
      clearStore()
      activeSyncUid = uid
      recipeProgressStore.initSync(uid)

      // Persist basic user profile metadata in user_data/{uid} for admin visibility & sync
      if (authStore.user?.email) {
        setDoc(doc(db, 'user_data', uid), {
          email: authStore.user.email,
          displayName: authStore.user.displayName || '',
          photoURL: authStore.userPhotoURL || authStore.user.photoURL || '',
          lastSeen: new Date().toISOString()
        }, { merge: true }).catch((err) => {
          console.warn('Could not update user_data profile:', err)
        })
      }

      const savedListId = getPersistedListId()
      if (savedListId) {
        activeListId.value = savedListId
      }

      const savedBookId = getPersistedBookId()
      if (savedBookId) {
        activeBookId.value = savedBookId
      }

      // Restore user-scoped active book & list from Firestore user_data if available
      getDoc(doc(db, 'user_data', uid)).then((snap) => {
        if (snap.exists()) {
          const data = snap.data()
          if (data?.lastActiveBookId) {
            safeLocalStorageSet(`activeBookId_${uid}`, data.lastActiveBookId)
            safeLocalStorageSet('activeBookId', data.lastActiveBookId)
            if (!activeBookId.value || activeBookId.value === 'demo-book') {
              activeBookId.value = data.lastActiveBookId
            }
          }
          if (data?.lastActiveListId) {
            safeLocalStorageSet(`activeListId_${uid}`, data.lastActiveListId)
            safeLocalStorageSet('activeListId', data.lastActiveListId)
            if (!activeListId.value || activeListId.value === 'demo-list') {
              activeListId.value = data.lastActiveListId
            }
          }
        }
      }).catch(() => {})

    // Helper for syncing collections
    const syncCollection = <T>(colName: string, stateRef: any) => {
      const q = query(collection(db, colName), where('ownerId', '==', uid))
      const unsub = onSnapshot(q, (snapshot) => {
        const docs = snapshot.docs.map(doc => doc.data() as T)
        // Sort items client-side if they have an 'order' property
        docs.sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
        stateRef.value = docs
      }, (err) => {
        // Only show toast for actual auth expiry, not for permission issues
        if (err?.code === 'unauthenticated') {
          showToast('Sitzung abgelaufen – bitte neu anmelden.', 'error')
        }
      })
      unsubscribes.push(unsub)
    }

    syncCollection<ArticleCategory>('articleCategories', articleCategories)
    syncCollection<Article>('articles', articles)
    syncCollection<RecipeCategory>('recipeCategories', recipeCategories)
    syncCollection<DeletedItem>('trashBin', trashBin)

    // Sync recipes (owned by user OR shared with user)
    let userEmail = authStore.user?.email?.toLowerCase() || ''
    const qRecipes = userEmail
      ? query(
          collection(db, 'recipes'),
          or(
            where('ownerId', '==', uid),
            where('sharedWith', 'array-contains', userEmail)
          )
        )
      : query(collection(db, 'recipes'), where('ownerId', '==', uid))

    const unsubRecipes = onSnapshot(qRecipes, (snapshot) => {
      const docs = snapshot.docs.map(doc => doc.data() as Recipe)
      docs.sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
      recipes.value = docs
      hasInitialSyncCompleted.value = true
    }, (err) => {
      hasInitialSyncCompleted.value = true
      if (err?.code === 'unauthenticated') {
        showToast('Sitzung abgelaufen – bitte neu anmelden.', 'error')
      }
    })
    unsubscribes.push(unsubRecipes)

    // Sync pending recipe invitations for this user
    if (userEmail) {
      const qRecInvites = query(
        collection(db, 'recipe_invitations'),
        where('invitedEmail', '==', userEmail),
        where('status', '==', 'pending')
      )
      const unsubRecInvites = onSnapshot(qRecInvites, (snapshot) => {
        pendingRecipeInvitations.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      }, (err) => {
        if (err?.code === 'unauthenticated') {
          showToast('Sitzung abgelaufen – bitte neu anmelden.', 'error')
        }
      })
      unsubscribes.push(unsubRecInvites)

      // Sync recipe books where ownerId == uid OR sharedWith array-contains userEmail
      const qBooks = query(
        collection(db, 'recipe_books'),
        or(
          where('ownerId', '==', uid),
          where('sharedWith', 'array-contains', userEmail)
        )
      )

      let isCreatingDefaultBook = false
      const unsubBooks = onSnapshot(qBooks, async (snap) => {
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        const displayName = authStore.user?.displayName || authStore.user?.email || 'Besitzer'
        const photoURL = authStore.userPhotoURL || authStore.user?.photoURL || ''

        // If no owned recipe book exists yet, create default one!
        // Guard prevents re-entrant calls while the write is in-flight
        const hasOwnedBook = docs.some((b: any) => b.ownerId === uid)
        if (!hasOwnedBook && !isCreatingDefaultBook) {
          isCreatingDefaultBook = true
          try {
            const defaultBookId = 'book-' + uid
            const initialBook = {
              id: defaultBookId,
              name: 'Persönliches Kochbuch',
              ownerId: uid,
              ownerEmail: userEmail,
              sharedWith: [],
              pendingInvites: [],
              members: [{ uid, email: userEmail, displayName, photoURL }],
              order: 0,
              createdAt: Date.now()
            }
            await setDoc(doc(db, 'recipe_books', defaultBookId), initialBook)
            docs.push(initialBook)
          } catch (err) {
            console.error('Error creating default recipe book:', err)
          } finally {
            isCreatingDefaultBook = false
          }
        } else if (!hasOwnedBook && isCreatingDefaultBook) {
          // Write still in-flight — skip this intermediate snapshot
          return
        }

        const canonicalDefaultBookId = 'book-' + uid
        // Guard: A shared book ("Geteilte Liste") must NEVER be chosen as fallback over personal book
        const personalBook = docs.find((b: any) => b.id === canonicalDefaultBookId)
          || docs.find((b: any) => b.name === 'Persönlich' || b.name === 'Persönliches Kochbuch' || !b.name)
          || docs.find((b: any) => b.ownerId === uid && !b.name?.toLowerCase().includes('geteilt'))
          || docs.find((b: any) => b.order === 0 && !b.name?.toLowerCase().includes('geteilt'))
          || docs.find((b: any) => !b.name?.toLowerCase().includes('geteilt'))
          || docs[0]
        const defaultBookId = personalBook ? personalBook.id : canonicalDefaultBookId

        docs.sort((a: any, b: any) => {
          const orderA = typeof a.order === 'number' ? a.order : (a.id === defaultBookId ? 0 : 999999)
          const orderB = typeof b.order === 'number' ? b.order : (b.id === defaultBookId ? 0 : 999999)
          if (orderA !== orderB) return orderA - orderB
          if (a.id === defaultBookId) return -1
          if (b.id === defaultBookId) return 1
          return (a.createdAt || 0) - (b.createdAt || 0)
        })

        recipeBooks.value = docs

        const savedBookId = getPersistedBookId()
        const currentTargetId = (activeBookId.value && activeBookId.value !== 'demo-book')
          ? activeBookId.value
          : savedBookId
        const exists = docs.some((b: any) => b.id === currentTargetId)
        if (exists && currentTargetId) {
          activeBookId.value = currentTargetId
        } else {
          activeBookId.value = defaultBookId
        }
      }, (err) => {
        if (err?.code === 'unauthenticated') {
          showToast('Sitzung abgelaufen – bitte neu anmelden.', 'error')
        }
      })
      unsubscribes.push(unsubBooks)

      const qBookInvites = query(
        collection(db, 'recipe_book_invitations'),
        where('invitedEmail', '==', userEmail),
        where('status', '==', 'pending')
      )
      const unsubBookInvites = onSnapshot(qBookInvites, (snapshot) => {
        const invites = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        pendingRecipeBookInvitations.value = invites
        try {
          const { notifySharedRecipeBook } = useNotifications()
          invites.forEach((invite: any) => {
            notifySharedRecipeBook(invite)
            addNotification({
              id: `invite-book-${invite.id}`,
              type: 'invite-book',
              title: 'Neues Kochbuch geteilt 📖',
              message: `${invite.invitedByName || invite.invitedByEmail || 'Jemand'} hat das Kochbuch „${invite.bookName || 'Kochbuch'}“ mit dir geteilt.`,
              url: '/recipes',
              data: invite
            })
          })
        } catch (e) {
          console.error('Failed to notify shared recipe book:', e)
        }
      }, (err) => {
        if (err?.code === 'unauthenticated') {
          showToast('Sitzung abgelaufen – bitte neu anmelden.', 'error')
        }
      })
      unsubscribes.push(unsubBookInvites)
    }

    const cleanupExpiredPixabayUrls = async () => {
      try {
        for (const rec of recipes.value) {
          if (rec.img && rec.img.includes('pixabay.com/get/')) {
            rec.img = ''
            updateDoc(doc(db, 'recipes', rec.id), { img: '' }).catch(() => {})
          }
        }
        for (const art of articles.value) {
          if (art.img && art.img.includes('pixabay.com/get/')) {
            art.img = ''
            updateDoc(doc(db, 'articles', art.id), { img: '' }).catch(() => {})
          }
        }
      } catch (e) {
        // ignore cleanup errors
      }
    }

    const migrateDominantColors = async () => {
      try {
        for (const rec of recipes.value) {
          if (rec.img && !rec.dominantColor) {
            if (rec.img.includes('pixabay.com/get/')) continue
            const fullImgUrl = getImageUrl(rec.img, 'recipe')
            const color = await extractDominantColor(fullImgUrl)
            if (color && color !== '139, 195, 75') {
              rec.dominantColor = color
              updateDoc(doc(db, 'recipes', rec.id), { dominantColor: color }).catch(() => {})
            }
          }
        }
        for (const art of articles.value) {
          if (art.img && !art.dominantColor) {
            if (art.img.includes('pixabay.com/get/')) continue
            const fullImgUrl = getImageUrl(art.img, 'article')
            const color = await extractDominantColor(fullImgUrl)
            if (color && color !== '139, 195, 75') {
              art.dominantColor = color
              updateDoc(doc(db, 'articles', art.id), { dominantColor: color }).catch(() => {})
            }
          }
        }
      } catch (e) {
        // ignore background migration errors
      }
    }

    const cleanupExpiredTrash = async (maxAgeDays = 30) => {
      try {
        if (!trashBin.value || trashBin.value.length === 0) return
        const cutoff = Date.now() - (maxAgeDays * 24 * 60 * 60 * 1000)
        const expiredItems = trashBin.value.filter(item => item.deletedAt && item.deletedAt < cutoff)
        if (expiredItems.length === 0) return

        const batchPromises = expiredItems.map(item => deleteDoc(doc(db, 'trashBin', item.id)))
        await Promise.all(batchPromises)
      } catch (err) {
        console.error('Automatisches Aufräumen des Papierkorbs fehlgeschlagen:', err)
      }
    }

    setTimeout(() => {
      cleanupExpiredPixabayUrls()
      migrateDominantColors()
      cleanupExpiredTrash(30)
    }, 1500)

    // Watch shoppingLists to maintain real-time unchecked item count listeners
    const stopWatchCounts = watch(() => shoppingLists.value, (newLists) => {
      const newListIds = new Set(newLists.map(l => l.id))
      
      // Unsubscribe from lists no longer present
      for (const [id, unsub] of countUnsubs.entries()) {
        if (!newListIds.has(id)) {
          unsub()
          countUnsubs.delete(id)
          delete listUncheckedCounts.value[id]
        }
      }
      
      // Subscribe to new lists
      newLists.forEach(list => {
        if (!countUnsubs.has(list.id)) {
          const isOwned = list.ownerId === uid
          const q = isOwned
            ? query(
                collection(db, 'shoppingList'),
                where('ownerId', '==', uid),
                where('listId', '==', list.id),
                where('checked', '==', false)
              )
            : query(
                collection(db, 'shoppingList'),
                where('listId', '==', list.id),
                where('checked', '==', false)
              )

          const unsub = onSnapshot(q, (snapshot) => {
            const count = snapshot.docs.filter(d => {
              const data = d.data()
              return data.articleID !== '__LIST__' && data.articleID !== '__INVITATION__'
            }).length
            listUncheckedCounts.value[list.id] = count
          }, (_err) => {
            // Gracefully set 0 if user lost access to a shared list or list was removed
            listUncheckedCounts.value[list.id] = 0
          })
          countUnsubs.set(list.id, unsub)
        }
      })
    }, { deep: true })
    unsubscribes.push(stopWatchCounts)

    userEmail = authStore.user?.email?.toLowerCase() || ''

    // Sync shopping lists (owned or shared)
    const qLists = userEmail
      ? query(
          collection(db, 'shoppingList'),
          and(
            where('articleID', '==', '__LIST__'),
            or(
              where('ownerId', '==', uid),
              where('sharedWith', 'array-contains', userEmail)
            )
          )
        )
      : query(
          collection(db, 'shoppingList'),
          where('articleID', '==', '__LIST__'),
          where('ownerId', '==', uid)
        )

    let isCreatingDefaultList = false
    const unsubLists = onSnapshot(qLists, async (snapshot) => {
      let docs = snapshot.docs.map(doc => doc.data())
      const canonicalDefaultListId = 'shop-list-default-' + uid

      // 1. Auto-Deduplication: Merge duplicate personal lists if any exist for this owner
      const ownedPersonalLists = docs.filter((l: any) =>
        l.ownerId === uid &&
        (!l.sharedWith || l.sharedWith.length === 0) &&
        (l.id === canonicalDefaultListId || l.id === uid || l.name === 'Persönliche Liste' || l.name === 'Persönlich' || !l.name)
      )

      if (ownedPersonalLists.length > 1) {
        const keeper = ownedPersonalLists.find((l: any) => l.id === canonicalDefaultListId)
          || ownedPersonalLists.find((l: any) => l.name === 'Persönlich')
          || ownedPersonalLists[0]

        const duplicates = ownedPersonalLists.filter((l: any) => l.id !== keeper.id)

        for (const dup of duplicates) {
          try {
            // Re-assign any items belonging to the duplicate list to the keeper
            const qDupItems = query(
              collection(db, 'shoppingList'),
              where('listId', '==', dup.id)
            )
            const dupSnap = await getDocs(qDupItems)
            if (!dupSnap.empty) {
              const batch = writeBatch(db)
              dupSnap.docs.forEach((itemDoc) => {
                if (itemDoc.data().articleID !== '__LIST__' && itemDoc.data().articleID !== '__INVITATION__') {
                  batch.update(doc(db, 'shoppingList', itemDoc.id), { listId: keeper.id })
                }
              })
              await batch.commit()
            }
            // Delete the duplicate list metadata document
            await deleteDoc(doc(db, 'shoppingList', dup.id))
          } catch (err) {
            console.error('Error merging duplicate shopping list:', err)
          }
        }

        const duplicateIds = new Set(duplicates.map((d: any) => d.id))
        docs = docs.filter((l: any) => !duplicateIds.has(l.id))
      }

      // 2. If no default list exists for this user, create it with deterministic ID!
      const hasOwnedList = docs.some((l: any) => l.ownerId === uid)
      if (!hasOwnedList && !isCreatingDefaultList) {
        isCreatingDefaultList = true
        try {
          const displayName = authStore.user?.displayName || authStore.user?.email || 'Persönlich'
          const email = authStore.user?.email?.toLowerCase() || ''
          const photoURL = authStore.userPhotoURL || authStore.user?.photoURL || ''

          const defaultListData = {
            id: canonicalDefaultListId,
            articleID: '__LIST__',
            listId: '__LIST_METADATA__',
            name: 'Persönliche Liste',
            ownerId: uid,
            ownerEmail: email,
            sharedWith: [],
            pendingInvites: [],
            members: [
              { uid, email, displayName, photoURL }
            ],
            order: 0,
            createdAt: Date.now(),
            updatedAt: Date.now(),
            amount: '',
            checked: false,
            note: ''
          }
          await setDoc(doc(db, 'shoppingList', canonicalDefaultListId), defaultListData)
          docs.push(defaultListData)
        } catch (err) {
          console.error('Error creating default list:', err)
        } finally {
          isCreatingDefaultList = false
        }
        return
      }

      // Find default list ID (canonical personal list)
      // Guard: A shared shopping list must never be chosen as fallback over personal list
      const personalList = docs.find((l: any) => l.id === canonicalDefaultListId || l.id === uid)
        || docs.find((l: any) => l.name === 'Persönliche Liste' || l.name === 'Persönlich' || !l.name)
        || docs.find((l: any) => l.ownerId === uid && !l.name?.toLowerCase().includes('geteilt'))
        || docs.find((l: any) => l.order === 0 && !l.name?.toLowerCase().includes('geteilt'))
        || docs.find((l: any) => !l.name?.toLowerCase().includes('geteilt'))
        || docs[0]
      const defaultListId = personalList ? personalList.id : (docs[0]?.id || uid)

      docs.sort((a: any, b: any) => {
        const orderA = typeof a.order === 'number' ? a.order : (a.id === defaultListId ? 0 : 99999999)
        const orderB = typeof b.order === 'number' ? b.order : (b.id === defaultListId ? 0 : 99999999)
        if (orderA !== orderB) return orderA - orderB
        if (a.id === defaultListId) return -1
        if (b.id === defaultListId) return 1
        return (a.createdAt || 0) - (b.createdAt || 0)
      })
      shoppingLists.value = docs

      const savedListId = getPersistedListId()
      const currentTargetId = (activeListId.value && activeListId.value !== 'demo-list')
        ? activeListId.value
        : savedListId
      const exists = docs.some((l: any) => l.id === currentTargetId)
      if (exists && currentTargetId) {
        activeListId.value = currentTargetId
      } else {
        activeListId.value = defaultListId
      }
      syncShoppingList()
      syncRecurringPurchases()

      // Prefetch items for other shopping lists in background so switching/swiping is instant
      if (uid) {
        const otherLists = docs.filter((l: any) => l.id && l.id !== activeListId.value)
        for (const oList of otherLists) {
          if (!getCachedShoppingList(oList.id)) {
            const isShared = (oList.sharedWith && oList.sharedWith.length > 0) || oList.ownerId !== uid
            const qPrefetch = isShared
              ? query(collection(db, 'shoppingList'), where('listId', '==', oList.id))
              : query(collection(db, 'shoppingList'), where('ownerId', '==', uid), where('listId', '==', oList.id))
            getDocs(qPrefetch).then(snap => {
              const prefetchedItems = snap.docs
                .map(d => d.data() as ShoppingCartItem)
                .filter(item => item.articleID !== '__LIST__' && item.articleID !== '__INVITATION__')
              prefetchedItems.sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
              if (!getCachedShoppingList(oList.id)) {
                setCachedShoppingList(oList.id, prefetchedItems)
              }
            }).catch(() => {})
          }
        }
      }
    }, (err) => {
      if (err?.code === 'unauthenticated') {
        showToast('Sitzung abgelaufen – bitte neu anmelden.', 'error')
      }
    })
    unsubscribes.push(unsubLists)

    // Sync pending invitations for this user
    if (userEmail) {
      const qInvites = query(
        collection(db, 'shoppingList'),
        where('articleID', '==', '__INVITATION__'),
        where('invitedEmail', '==', userEmail),
        where('status', '==', 'pending')
      )
      const unsubInvites = onSnapshot(qInvites, (snapshot) => {
        const invites = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        pendingInvitations.value = invites
        try {
          const { notifySharedShoppingList } = useNotifications()
          invites.forEach((invite: any) => {
            notifySharedShoppingList(invite)
            addNotification({
              id: `invite-list-${invite.id}`,
              type: 'invite-shopping',
              title: 'Neue Einkaufsliste geteilt 🛒',
              message: `${invite.invitedByName || invite.invitedByEmail || 'Jemand'} hat die Einkaufsliste „${invite.listName || 'Einkaufsliste'}“ mit dir geteilt.`,
              url: '/shopping-list',
              data: invite
            })
          })
        } catch (e) {
          console.error('Failed to notify shared shopping list:', e)
        }
      }, (err) => {
        if (err?.code === 'unauthenticated') {
          showToast('Sitzung abgelaufen – bitte neu anmelden.', 'error')
        }
      })
      unsubscribes.push(unsubInvites)
    }

    // Wait for lists snapshot to resolve activeListId first
    // activeListId will be set automatically inside unsubLists snapshot handler

    // User Data (Settings, Units)
    const userDataRef = doc(db, 'user_data', uid)
    const unsubUserData = onSnapshot(userDataRef, async (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data()
        // Migrate / normalize stored units — handles three formats:
        //  1. Legacy string[]  → { name: u, forShopping: true, forRecipes: true }
        //  2. Correct Unit{}   → pass through as-is
        //  3. Corrupted nested → { name: {name:"X",...} } → flatten
        const normalizeUnit = (u: any): Unit => {
          if (typeof u === 'string') {
            return { name: u, forShopping: true, forRecipes: true }
          }
          // Flatten nested Unit objects (name is itself a Unit object)
          const rawName = u.name
          const name: string = typeof rawName === 'string'
            ? rawName
            : (typeof rawName === 'object' && rawName !== null ? rawName.name ?? '' : String(rawName ?? ''))
          return { name, forShopping: u.forShopping ?? true, forRecipes: u.forRecipes ?? true }
        }
        const normalized = (data.units || []).map(normalizeUnit)
        units.value = normalized
        // Persist normalized data back if any entry needed fixing
        const hadCorruption = (data.units || []).some((u: any) => typeof u !== 'object' || typeof u?.name !== 'string')
        if (hadCorruption) {
          updateUserData({ units: normalized }).catch(() => {})
        }
        activeHeaderColor.value = data.activeHeaderColor || null
        includeCheckedInCopy.value = data.includeCheckedInCopy ?? false
        shoppingListSlim.value = data.shoppingListSlim ?? false
        shoppingListHideHeaders.value = data.shoppingListHideHeaders ?? false
        watchTargetListId.value = data.watchTargetListId || ''
        watchVibrationEnabled.value = data.watchVibrationEnabled ?? true
        watchVoiceFeedbackEnabled.value = data.watchVoiceFeedbackEnabled ?? true
        watchAutoListenOnLaunch.value = data.watchAutoListenOnLaunch ?? false
        watchKeepScreenOnCooking.value = data.watchKeepScreenOnCooking ?? true
        watchKeepScreenOnShopping.value = data.watchKeepScreenOnShopping ?? true
        watchDimAmbientMode.value = data.watchDimAmbientMode ?? true

        if (data.onboardingCompleted === true) {
          isOnboardingOpen.value = false
        } else {
          // Race condition guard: user_data snapshot may fire before articles/recipes snapshots.
          // Check Firestore directly instead of relying on potentially-empty local arrays.
          try {
            const [artSnap, recSnap] = await Promise.all([
              getDocs(query(collection(db, 'articles'), where('ownerId', '==', uid), limit(1))),
              getDocs(query(collection(db, 'recipes'), where('ownerId', '==', uid), limit(1))),
            ])
            const hasExistingData = !artSnap.empty || !recSnap.empty
            if (hasExistingData) {
              updateUserData({ onboardingCompleted: true }).catch(() => {})
              isOnboardingOpen.value = false
            } else {
              isOnboardingOpen.value = true
            }
          } catch {
            // If Firestore check fails, don't show onboarding — safer to skip
            isOnboardingOpen.value = false
          }
        }
      } else {
        units.value = []
        activeHeaderColor.value = null
        includeCheckedInCopy.value = false
        shoppingListSlim.value = false
        shoppingListHideHeaders.value = false
        // Race condition guard: check Firestore directly
        try {
          const [artSnap, recSnap] = await Promise.all([
            getDocs(query(collection(db, 'articles'), where('ownerId', '==', uid), limit(1))),
            getDocs(query(collection(db, 'recipes'), where('ownerId', '==', uid), limit(1))),
          ])
          if (artSnap.empty && recSnap.empty) {
            isOnboardingOpen.value = true
          }
        } catch {
          // Fail silently — don't show onboarding if check fails
        }
      }
    }, (err) => {
      if (err?.code === 'unauthenticated') {
        showToast('Sitzung abgelaufen – bitte neu anmelden.', 'error')
      }
    })
    unsubscribes.push(unsubUserData)

    // Global Config for Image APIs
    const imageConfigRef = doc(db, 'system_config', 'image_api')
    const unsubImageConfig = onSnapshot(imageConfigRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data()
        pixabayApiKey.value = data.pixabayApiKey || ''
        pexelsApiKey.value = data.pexelsApiKey || ''
        unsplashAccessKey.value = data.unsplashAccessKey || ''
        unsplashAppId.value = data.unsplashAppId || ''
        unsplashSecret.value = data.unsplashSecret || ''
      } else {
        pixabayApiKey.value = ''
        pexelsApiKey.value = ''
        unsplashAccessKey.value = ''
        unsplashAppId.value = ''
        unsplashSecret.value = ''
      }
    }, () => {})
    unsubscribes.push(unsubImageConfig)

    // Global Config for System / Support URL
    const appConfigRef = doc(db, 'system_config', 'app_config')
    const unsubAppConfig = onSnapshot(appConfigRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data()
        if (data.supportUrl) {
          supportUrl.value = data.supportUrl
        }
      }
    }, () => {})
    unsubscribes.push(unsubAppConfig)
  } finally {
    isInitializing = false
  }
}

  // Helper for Owner ID
  const getUid = () => {
    const uid = useAuthStore().user?.uid
    if (!uid) {
      if (isDemoMode.value) return 'demo-user'
      throw new Error('Not authenticated')
    }
    return uid
  }

  // --- ARTIKEL KATEGORIEN ACTIONS ---
  const addArticleCategory = async (category: Omit<ArticleCategory, 'id'>) => {
    if (articleCategories.value.length >= QUOTA_CONFIG.MAX_CATEGORIES_PER_USER) {
      showToast(`Maximale Anzahl an Artikel-Kategorien erreicht (${QUOTA_CONFIG.MAX_CATEGORIES_PER_USER}).`, 'error')
      return null
    }
    const uid = getUid()
    const id = 'cat-' + Date.now().toString() + '-' + Math.random().toString(36).substring(2, 9)
    const maxOrder = articleCategories.value.reduce((max, cat) => Math.max(max, typeof cat.order === 'number' ? cat.order : 0), 0)
    const order = typeof category.order === 'number' ? category.order : (articleCategories.value.length > 0 ? maxOrder + 1 : 0)
    const newCat: ArticleCategory = {
      ...category,
      name: sanitizeTextInput(category.name, 100),
      img: sanitizeUrl(category.img),
      ...(category.originalImg ? { originalImg: sanitizeUrl(category.originalImg) } : {}),
      id,
      ownerId: uid,
      order
    }
    if (isDemoMode.value) {
      articleCategories.value.push(newCat)
      return newCat
    }
    await setDoc(doc(db, 'articleCategories', id), newCat)
    if (!articleCategories.value.some(c => c.id === id)) {
      articleCategories.value.push(newCat)
    }
    return newCat
  }

  const updateArticleCategory = async (category: ArticleCategory) => {
    const cleanedCat: any = {
      ...category,
      name: sanitizeTextInput(category.name, 100),
      img: sanitizeUrl(category.img)
    }
    if (category.originalImg !== undefined) {
      cleanedCat.originalImg = sanitizeUrl(category.originalImg)
    }
    const idx = articleCategories.value.findIndex(c => c.id === category.id)
    if (idx !== -1) {
      articleCategories.value[idx] = { ...articleCategories.value[idx], ...cleanedCat }
      articleCategories.value = [...articleCategories.value]
    }
    if (isDemoMode.value) return
    try {
      await updateDoc(doc(db, 'articleCategories', category.id), { ...cleanedCat })
    } catch (e) {
      // Safe fallback for offline / test mode
    }
  }

  const moveToTrash = async (
    type: 'articleCategory' | 'article' | 'recipeCategory' | 'recipe' | 'shoppingItem',
    item: any
  ): Promise<string | null> => {
    if (!item) return null
    const uid = getUid()
    const trashId = 'trash-' + Date.now().toString()
    const trashEntry: DeletedItem = {
      id: trashId,
      type,
      payload: item,
      deletedAt: Date.now()
    }

    // Enforce max trash items quota (prune oldest if exceeding)
    const maxItems = QUOTA_CONFIG.TRASH.MAX_ITEMS
    if (trashBin.value.length >= maxItems) {
      const itemsToEvict = trashBin.value.slice(maxItems - 1)
      trashBin.value = trashBin.value.slice(0, maxItems - 1)
      if (!isDemoMode.value) {
        itemsToEvict.forEach(async (oldItem) => {
          try {
            await deleteDoc(doc(db, 'trashBin', oldItem.id))
          } catch {
            // ignore
          }
        })
      }
    }

    trashBin.value = [trashEntry, ...trashBin.value]
    if (isDemoMode.value) return trashId
    try {
      await setDoc(doc(db, 'trashBin', trashId), { ...trashEntry, ownerId: uid, originalId: item.id })
    } catch (err) {
      console.error('Failed to write trash item to firestore:', err)
    }
    return trashId
  }

  const reorderCollection = async (collectionName: string, items: { id: string }[]) => {
    if (collectionName === 'recipeCategories') {
      items.forEach((item, idx) => {
        const match = recipeCategories.value.find(c => c.id === item.id)
        if (match) match.order = idx
      })
      recipeCategories.value = [...(items as RecipeCategory[])]
    } else if (collectionName === 'articleCategories') {
      items.forEach((item, idx) => {
        const match = articleCategories.value.find(c => c.id === item.id)
        if (match) match.order = idx
      })
      articleCategories.value = [...(items as ArticleCategory[])]
    } else if (collectionName === 'articles') {
      items.forEach((item, idx) => {
        const match = articles.value.find(a => a.id === item.id)
        if (match) match.order = idx
      })
      articles.value = [...(items as Article[])]
    }

    if (isDemoMode.value) return
    try {
      const CHUNK_SIZE = 400
      for (let i = 0; i < items.length; i += CHUNK_SIZE) {
        const chunk = items.slice(i, i + CHUNK_SIZE)
        const batch = writeBatch(db)
        chunk.forEach((item, chunkIdx) => {
          batch.update(doc(db, collectionName, item.id), { order: i + chunkIdx })
        })
        await batch.commit()
      }
    } catch (e) {
      console.error(`Failed to commit atomic reorder batch for ${collectionName}:`, e)
    }
  }

  const deleteArticleCategory = async (id: string, customToast?: string) => {
    const item = articleCategories.value.find(c => c.id === id)
    const trashId = await moveToTrash('articleCategory', item)
    articleCategories.value = articleCategories.value.filter(c => c.id !== id)
    if (!isDemoMode.value) {
      try {
        await deleteDoc(doc(db, 'articleCategories', id))
      } catch (e) {
        console.error(e)
      }
    }
    if (item) {
      const msg = customToast || `Kategorie "${item.name}" in Papierkorb verschoben`
      showToast(msg, 'success', {
        label: 'Rückgängig',
        callback: async () => {
          if (trashId) {
            await restoreFromTrash(trashId)
          } else if (item.id) {
            if (!isDemoMode.value) {
              await setDoc(doc(db, 'articleCategories', item.id), item)
            }
            if (!articleCategories.value.some(c => c.id === item.id)) {
              articleCategories.value.push(item)
            }
          }
          showToast(`Kategorie "${item.name}" wiederhergestellt`, 'success')
        }
      })
    }
  }

  const reorderArticleCategories = async (categories: ArticleCategory[]) => {
    categories.forEach((cat, idx) => {
      cat.order = idx
    })
    articleCategories.value = [...categories]
    await reorderCollection('articleCategories', categories)
  }

  const reorderArticles = async (newArticles: Article[]) => {
    newArticles.forEach((art, idx) => {
      art.order = idx
    })
    articles.value = [...newArticles]
    await reorderCollection('articles', newArticles)
  }

  // --- ARTIKEL / ZUTATEN ACTIONS ---
  const sanitizeForFirestore = <T extends Record<string, any>>(obj: T): T => {
    const cleaned: Record<string, any> = {}
    Object.entries(obj).forEach(([key, val]) => {
      if (val !== undefined) {
        cleaned[key] = val
      }
    })
    return cleaned as T
  }

  const addArticle = async (article: Omit<Article, 'id'>) => {
    if (articles.value.length >= QUOTA_CONFIG.MAX_ARTICLES_PER_USER) {
      showToast(`Maximale Anzahl an Artikeln erreicht (${QUOTA_CONFIG.MAX_ARTICLES_PER_USER}).`, 'error')
      return null
    }
    const uid = getUid()
    const id = 'art-' + Date.now().toString() + '-' + Math.random().toString(36).substring(2, 9)
    const maxOrder = articles.value.reduce((max, a) => Math.max(max, typeof a.order === 'number' ? a.order : 0), 0)
    const order = typeof article.order === 'number' ? article.order : (articles.value.length > 0 ? maxOrder + 1 : 0)
    const sanitizedAliases = Array.isArray(article.aliases)
      ? Array.from(new Set(
          article.aliases
            .map(a => sanitizeTextInput(a, 100).trim())
            .filter(Boolean)
        ))
      : undefined

    const sanitizedName = cleanArticleName(sanitizeTextInput(article.name, 100))
    if (!sanitizedName) return null

    const newArt = {
      ...article,
      name: sanitizedName,
      aliases: sanitizedAliases,
      note: sanitizeTextInput(article.note, 500),
      unit: sanitizeTextInput(article.unit, 20),
      shoppingUnit: article.shoppingUnit !== undefined ? sanitizeTextInput(article.shoppingUnit, 20) : undefined,
      recipeUnit: article.recipeUnit !== undefined ? sanitizeTextInput(article.recipeUnit, 20) : undefined,
      lastShoppingUnit: article.lastShoppingUnit !== undefined ? sanitizeTextInput(article.lastShoppingUnit, 20) : undefined,
      lastRecipeUnit: article.lastRecipeUnit !== undefined ? sanitizeTextInput(article.lastRecipeUnit, 20) : undefined,
      defaultListId: article.defaultListId ? sanitizeTextInput(article.defaultListId, 100) : undefined,
      img: sanitizeUrl(article.img),
      ...(article.originalImg ? { originalImg: sanitizeUrl(article.originalImg) } : {}),
      id,
      ownerId: uid,
      lastUsed: Date.now(),
      order
    }
    const cleaned = sanitizeForFirestore(newArt)
    if (isDemoMode.value) {
      articles.value.push(cleaned as Article)
      return cleaned as Article
    }
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine
    if (isOffline) {
      setDoc(doc(db, 'articles', id), cleaned).catch(() => {})
      if (!articles.value.some(a => a.id === id)) {
        articles.value.push(cleaned as Article)
      }
      return cleaned as Article
    }
    if (import.meta.env?.TEST) {
      await setDoc(doc(db, 'articles', id), cleaned)
    } else {
      try {
        let timer: any
        const timeoutPromise = new Promise<void>((resolve) => {
          timer = setTimeout(resolve, 1500)
        })
        await Promise.race([
          setDoc(doc(db, 'articles', id), cleaned),
          timeoutPromise
        ])
        clearTimeout(timer)
      } catch (e) {
        // Safe fallback for offline / network issues
      }
    }
    if (!articles.value.some(a => a.id === id)) {
      articles.value.push(cleaned as Article)
    }
    return cleaned as Article
  }

  const updateArticle = async (article: Article) => {
    const sanitizedAliases = Array.isArray(article.aliases)
      ? Array.from(new Set(
          article.aliases
            .map(a => sanitizeTextInput(a, 100).trim())
            .filter(Boolean)
        ))
      : undefined

    const sanitizedName = cleanArticleName(sanitizeTextInput(article.name, 100)) || sanitizeTextInput(article.name, 100)

    const sanitizedArt: any = {
      ...article,
      name: sanitizedName,
      aliases: sanitizedAliases,
      note: sanitizeTextInput(article.note, 500),
      unit: sanitizeTextInput(article.unit, 20),
      shoppingUnit: article.shoppingUnit !== undefined ? sanitizeTextInput(article.shoppingUnit, 20) : undefined,
      recipeUnit: article.recipeUnit !== undefined ? sanitizeTextInput(article.recipeUnit, 20) : undefined,
      lastShoppingUnit: article.lastShoppingUnit !== undefined ? sanitizeTextInput(article.lastShoppingUnit, 20) : undefined,
      lastRecipeUnit: article.lastRecipeUnit !== undefined ? sanitizeTextInput(article.lastRecipeUnit, 20) : undefined,
      defaultListId: article.defaultListId !== undefined ? (sanitizeTextInput(article.defaultListId, 100) || '') : undefined,
      img: sanitizeUrl(article.img)
    }
    if (article.originalImg !== undefined) {
      sanitizedArt.originalImg = sanitizeUrl(article.originalImg)
    }
    const cleaned = sanitizeForFirestore(sanitizedArt) as Article
    const idx = articles.value.findIndex(a => a.id === article.id)
    if (idx !== -1) {
      articles.value[idx] = { ...articles.value[idx], ...cleaned }
      articles.value = [...articles.value]
    }
    if (isDemoMode.value) return
    const isOfflineUpdate = typeof navigator !== 'undefined' && !navigator.onLine
    if (isOfflineUpdate) {
      updateDoc(doc(db, 'articles', article.id), cleaned as Record<string, any>).catch(() => {})
      return
    }
    if (import.meta.env?.TEST) {
      await updateDoc(doc(db, 'articles', article.id), cleaned as Record<string, any>)
      return
    }
    try {
      let timer: any
      const timeoutPromise = new Promise<void>((resolve) => {
        timer = setTimeout(resolve, 1500)
      })
      await Promise.race([
        updateDoc(doc(db, 'articles', article.id), cleaned as Record<string, any>),
        timeoutPromise
      ])
      clearTimeout(timer)
    } catch (e) {
      // Safe fallback for offline / test mode
    }
  }

  const deleteArticle = async (id: string, silent = false, customToast?: string) => {
    const item = articles.value.find(a => a.id === id)
    const trashId = silent ? null : await moveToTrash('article', item)
    articles.value = articles.value.filter(a => a.id !== id)
    if (!isDemoMode.value) {
      try {
        await deleteDoc(doc(db, 'articles', id))
      } catch (e) {
        console.error(e)
      }
    }
    if (!silent && item) {
      const msg = customToast || `Artikel "${item.name}" in Papierkorb verschoben`
      showToast(msg, 'success', {
        label: 'Rückgängig',
        callback: async () => {
          if (trashId) {
            await restoreFromTrash(trashId)
          } else if (item.id) {
            if (!isDemoMode.value) {
              await setDoc(doc(db, 'articles', item.id), item)
            }
            if (!articles.value.some(a => a.id === item.id)) {
              articles.value.push(item)
            }
          }
          showToast(`Artikel "${item.name}" wiederhergestellt`, 'success')
        }
      })
    }
  }

  const rollbackImportedArticles = async (articleIds: string[]) => {
    if (!articleIds || articleIds.length === 0) return
    for (const id of articleIds) {
      // Only delete if the article is not referenced in any existing recipe or shopping item
      const isUsedInRecipe = recipes.value.some(r => r.ingredients?.some(i => i.articleID === id))
      const isUsedInShoppingList = shoppingList.value.some(i => i.articleID === id)
      if (!isUsedInRecipe && !isUsedInShoppingList) {
        await deleteArticle(id, true)
      }
    }
  }

  const updateUserData = async (data: any) => {
    if (data.shoppingListSlim !== undefined) shoppingListSlim.value = data.shoppingListSlim
    if (data.shoppingListHideHeaders !== undefined) shoppingListHideHeaders.value = data.shoppingListHideHeaders
    if (data.includeCheckedInCopy !== undefined) includeCheckedInCopy.value = data.includeCheckedInCopy
    if (data.units !== undefined) units.value = data.units
    if (data.watchTargetListId !== undefined) watchTargetListId.value = data.watchTargetListId
    if (data.watchVibrationEnabled !== undefined) watchVibrationEnabled.value = data.watchVibrationEnabled
    if (data.watchVoiceFeedbackEnabled !== undefined) watchVoiceFeedbackEnabled.value = data.watchVoiceFeedbackEnabled
    if (data.watchAutoListenOnLaunch !== undefined) watchAutoListenOnLaunch.value = data.watchAutoListenOnLaunch
    if (data.watchKeepScreenOnCooking !== undefined) watchKeepScreenOnCooking.value = data.watchKeepScreenOnCooking
    if (data.watchKeepScreenOnShopping !== undefined) watchKeepScreenOnShopping.value = data.watchKeepScreenOnShopping
    if (data.watchDimAmbientMode !== undefined) watchDimAmbientMode.value = data.watchDimAmbientMode
    if (isDemoMode.value) return
    const uid = getUid()
    await setDoc(doc(db, 'user_data', uid), data, { merge: true })
  }


  const addUnit = async (unit: Omit<Unit, never>) => {
    if (!units.value.some(u => u.name === unit.name)) {
      const newUnits: Unit[] = [...units.value, unit]
      await updateUserData({ units: newUnits })
    }
  }

  const deleteUnit = async (name: string) => {
    const newUnits = units.value.filter(u => u.name !== name)
    await updateUserData({ units: newUnits })
  }

  const reorderUnits = async (newUnits: Unit[]) => {
    units.value = newUnits
    await updateUserData({ units: newUnits })
  }

  const updateUnit = async (name: string, updates: Partial<Omit<Unit, 'name'>> & { name?: string }) => {
    const newName = updates.name ?? name
    const newUnits: Unit[] = units.value.map(u =>
      u.name === name ? { ...u, ...updates, name: newName } : u
    )
    await updateUserData({ units: newUnits })
    // If renamed, cascade to articles
    if (newName !== name) {
      const articlesToUpdate = articles.value.filter(art => art.unit === name)
      const updatePromises = articlesToUpdate.map(art =>
        updateDoc(doc(db, 'articles', art.id), { unit: newName })
      )
      await Promise.all(updatePromises)
    }
  }

  // Keep renameUnit as a convenience wrapper
  const renameUnit = async (oldName: string, newName: string) => {
    await updateUnit(oldName, { name: newName })
  }

  // --- REZEPT KATEGORIEN ACTIONS ---
  const addRecipeCategory = async (name: string, img: string = '', originalImg: string = '', thumb: string = '') => {
    if (recipeCategories.value.length >= QUOTA_CONFIG.MAX_CATEGORIES_PER_USER) {
      showToast(`Maximale Anzahl an Rezept-Kategorien erreicht (${QUOTA_CONFIG.MAX_CATEGORIES_PER_USER}).`, 'error')
      return null
    }
    const uid = getUid()
    const id = 'rcat-' + Date.now().toString() + '-' + Math.random().toString(36).substring(2, 9)
    const maxOrder = recipeCategories.value.reduce((max, cat) => Math.max(max, cat.order ?? 0), 0)
    const newCat: RecipeCategory = {
      id,
      name: sanitizeTextInput(name, 100),
      img: sanitizeUrl(img),
      ...(originalImg ? { originalImg: sanitizeUrl(originalImg) } : (img ? { originalImg: sanitizeUrl(img) } : {})),
      ...(thumb ? { thumb: sanitizeUrl(thumb) } : {}),
      order: maxOrder + 1
    }
    if (isDemoMode.value) {
      recipeCategories.value.push(newCat)
      return newCat
    }
    await setDoc(doc(db, 'recipeCategories', id), { ...newCat, ownerId: uid })
    if (!recipeCategories.value.some(c => c.id === id)) {
      recipeCategories.value.push(newCat)
    }
    return newCat
  }

  const deleteRecipeCategory = async (id: string, customToast?: string) => {
    const item = recipeCategories.value.find(c => c.id === id)
    const trashId = await moveToTrash('recipeCategory', item)
    recipeCategories.value = recipeCategories.value.filter(c => c.id !== id)
    if (!isDemoMode.value) {
      try {
        await deleteDoc(doc(db, 'recipeCategories', id))
      } catch (e) {
        console.error(e)
      }
    }
    if (item) {
      const msg = customToast || `Kategorie "${item.name}" in Papierkorb verschoben`
      showToast(msg, 'success', {
        label: 'Rückgängig',
        callback: async () => {
          if (trashId) {
            await restoreFromTrash(trashId)
          } else if (item.id) {
            if (!isDemoMode.value) {
              await setDoc(doc(db, 'recipeCategories', item.id), item)
            }
            if (!recipeCategories.value.some(c => c.id === item.id)) {
              recipeCategories.value.push(item)
            }
          }
          showToast(`Kategorie "${item.name}" wiederhergestellt`, 'success')
        }
      })
    }
  }

  const updateRecipeCategory = async (category: RecipeCategory) => {
    const cleanedCat: any = {
      name: sanitizeTextInput(category.name, 100),
      img: sanitizeUrl(category.img),
      order: category.order ?? 0
    }
    if (category.originalImg !== undefined) {
      cleanedCat.originalImg = sanitizeUrl(category.originalImg)
    }
    if (category.thumb !== undefined) {
      cleanedCat.thumb = sanitizeUrl(category.thumb)
    }
    const idx = recipeCategories.value.findIndex(c => c.id === category.id)
    if (idx !== -1) {
      recipeCategories.value[idx] = { ...recipeCategories.value[idx], ...cleanedCat }
      recipeCategories.value = [...recipeCategories.value]
    }
    if (isDemoMode.value) return
    try {
      await updateDoc(doc(db, 'recipeCategories', category.id), cleanedCat)
    } catch (e) {
      // Safe fallback for offline / test mode
    }
  }

  const reorderRecipeCategories = async (categories: RecipeCategory[]) => {
    categories.forEach((cat, idx) => {
      cat.order = idx
    })
    recipeCategories.value = [...categories]
    await reorderCollection('recipeCategories', categories)
  }

  // --- REZEPTE ACTIONS ---
  const addRecipe = async (recipe: Omit<Recipe, 'id' | 'favorite'>) => {
    if (recipes.value.length >= QUOTA_CONFIG.MAX_RECIPES_PER_USER) {
      showToast(`Maximale Anzahl an Rezepten erreicht (${QUOTA_CONFIG.MAX_RECIPES_PER_USER}).`, 'error')
      return null
    }
    const uid = getUid()
    const authStore = useAuthStore()
    const id = 'rec-' + Date.now().toString() + '-' + Math.random().toString(36).substring(2, 9)
    const email = authStore.user?.email?.toLowerCase() || ''
    const displayName = authStore.user?.displayName || authStore.user?.email || 'Ersteller'
    const photoURL = authStore.userPhotoURL || authStore.user?.photoURL || ''

    const currentBook = recipeBooks.value.find(b => b.id === activeBookId.value)
    const targetBookIds = (recipe.bookIds && recipe.bookIds.length > 0)
      ? recipe.bookIds
      : [recipe.bookId || activeBookId.value || ('book-' + uid)]

    const targetCategoryIds = (recipe.categoryIDs && recipe.categoryIDs.length > 0)
      ? recipe.categoryIDs
      : (recipe.categoryID ? [recipe.categoryID] : [])

    const sanitizedSteps = Array.isArray(recipe.steps)
      ? recipe.steps.map(s => typeof s === 'string' ? sanitizeTextInput(s, 2000) : { ...s, text: sanitizeTextInput(s.text, 2000) })
      : []

    const targetServings = (typeof recipe.servings === 'number' && recipe.servings > 0)
      ? Math.max(1, Math.min(100, Math.round(recipe.servings)))
      : 4

    const sanitizedTags = Array.isArray(recipe.tags)
      ? recipe.tags.map(t => sanitizeTextInput(t, 50).trim()).filter(Boolean)
      : []

    const sanitizedComment = typeof recipe.comment === 'string'
      ? sanitizeTextInput(recipe.comment, 5000).trim()
      : undefined

    const newRec: Recipe = {
      ...recipe,
      name: sanitizeTextInput(recipe.name, 150),
      img: sanitizeUrl(recipe.img),
      ...(recipe.originalImg ? { originalImg: sanitizeUrl(recipe.originalImg) } : (recipe.img ? { originalImg: sanitizeUrl(recipe.img) } : {})),
      ...(recipe.thumb ? { thumb: sanitizeUrl(recipe.thumb) } : {}),
      steps: sanitizedSteps,
      tags: sanitizedTags,
      ...(sanitizedComment ? { comment: sanitizedComment } : {}),
      id,
      favorite: false,
      servings: targetServings,
      categoryID: targetCategoryIds[0] || '',
      categoryIDs: targetCategoryIds,
      bookId: targetBookIds[0],
      bookIds: targetBookIds,
      dominantColor: recipe.dominantColor || undefined,
      ownerId: uid,
      ownerEmail: email,
      sharedWith: currentBook?.sharedWith || [],
      members: currentBook?.members || [{ uid, email, displayName, photoURL }]
    }
    const recordRecipeArticleUnits = async (ings: any[]) => {
      if (!Array.isArray(ings)) return
      for (const ing of ings) {
        if (ing?.articleID && ing?.amount) {
          const art = articles.value.find(a => a.id === ing.articleID)
          if (art) {
            const { unit: parsedUnit } = parseAmountAndUnit(ing.amount)
            const u = parsedUnit.trim()
            if (u && u !== art.lastRecipeUnit) {
              await updateArticle({ ...art, lastRecipeUnit: u })
            }
          }
        }
      }
    }

    await recordRecipeArticleUnits(recipe.ingredients)

    if (isDemoMode.value) {
      const recIdx = recipes.value.findIndex(r => r.id === id)
      if (recIdx === -1) {
        recipes.value.push(newRec)
      }
      return newRec
    }
    try {
      await setDoc(doc(db, 'recipes', id), newRec)
    } catch (e) {
      console.error('Failed to save recipe to Firestore:', e)
      showToast('Rezept konnte nicht gespeichert werden. Bitte erneut versuchen.', 'error')
      throw e
    }
    // Only add to local state AFTER Firestore write succeeds
    const recIdx = recipes.value.findIndex(r => r.id === id)
    if (recIdx === -1) {
      recipes.value.push(newRec)
    }
    return newRec
  }

  const updateRecipe = async (recipe: Recipe) => {
    const targetBookIds = (recipe.bookIds && recipe.bookIds.length > 0)
      ? recipe.bookIds
      : (recipe.bookId ? [recipe.bookId] : [activeBookId.value || ('book-' + getUid())])

    const targetCategoryIds = (recipe.categoryIDs && recipe.categoryIDs.length > 0)
      ? recipe.categoryIDs
      : (recipe.categoryID ? [recipe.categoryID] : [])

    const targetServings = (typeof recipe.servings === 'number' && recipe.servings > 0)
      ? Math.max(1, Math.min(100, Math.round(recipe.servings)))
      : 4

    const sanitizedSteps = Array.isArray(recipe.steps)
      ? recipe.steps.map(s => typeof s === 'string' ? sanitizeTextInput(s, 2000) : { ...s, text: sanitizeTextInput(s.text, 2000) })
      : []

    const sanitizedTags = Array.isArray(recipe.tags)
      ? recipe.tags.map(t => sanitizeTextInput(t, 50).trim()).filter(Boolean)
      : (recipe.tags ? [] : undefined)

    const sanitizedComment = typeof recipe.comment === 'string'
      ? sanitizeTextInput(recipe.comment, 5000).trim()
      : (recipe.comment === '' ? '' : undefined)

    const updatedRecipe: any = {
      ...recipe,
      name: sanitizeTextInput(recipe.name, 150),
      img: sanitizeUrl(recipe.img),
      ...(recipe.originalImg !== undefined ? { originalImg: sanitizeUrl(recipe.originalImg) } : {}),
      ...(recipe.thumb !== undefined ? { thumb: sanitizeUrl(recipe.thumb) } : {}),
      steps: sanitizedSteps,
      servings: targetServings,
      categoryID: targetCategoryIds[0] || '',
      categoryIDs: targetCategoryIds,
      ...(sanitizedTags !== undefined ? { tags: sanitizedTags } : {}),
      ...(sanitizedComment !== undefined ? { comment: sanitizedComment } : {}),
      bookId: targetBookIds[0],
      bookIds: targetBookIds
    }
    const idx = recipes.value.findIndex(r => r.id === recipe.id)
    if (idx !== -1) {
      recipes.value[idx] = { ...recipes.value[idx], ...updatedRecipe }
      recipes.value = [...recipes.value]
    }

    const recordRecipeArticleUnits = async (ings: any[]) => {
      if (!Array.isArray(ings)) return
      for (const ing of ings) {
        if (ing?.articleID && ing?.amount) {
          const art = articles.value.find(a => a.id === ing.articleID)
          if (art) {
            const { unit: parsedUnit } = parseAmountAndUnit(ing.amount)
            const u = parsedUnit.trim()
            if (u && u !== art.lastRecipeUnit) {
              await updateArticle({ ...art, lastRecipeUnit: u })
            }
          }
        }
      }
    }

    await recordRecipeArticleUnits(recipe.ingredients)

    if (isDemoMode.value) return
    try {
      await updateDoc(doc(db, 'recipes', recipe.id), updatedRecipe)
    } catch (e) {
      // Safe fallback for offline / test mode
    }
  }

  const deleteRecipe = async (id: string) => {
    const item = recipes.value.find(r => r.id === id)
    const trashId = await moveToTrash('recipe', item)
    recipes.value = recipes.value.filter(r => r.id !== id)
    if (!isDemoMode.value) {
      try {
        await deleteDoc(doc(db, 'recipes', id))

        const qInv = query(collection(db, 'recipe_invitations'), where('targetRecipeId', '==', id))
        const snap = await getDocs(qInv)
        snap.docs.forEach(d => deleteDoc(d.ref))
      } catch (e) {
        console.error(e)
      }
    }

    if (item) {
      showToast(`Rezept "${item.name}" in Papierkorb verschoben`, 'success', {
        label: 'Rückgängig',
        callback: async () => {
          if (trashId) {
            await restoreFromTrash(trashId)
          } else if (item.id) {
            if (!isDemoMode.value) {
              await setDoc(doc(db, 'recipes', item.id), item)
            }
            if (!recipes.value.some(r => r.id === item.id)) {
              recipes.value.push(item)
            }
          }
          showToast(`Rezept "${item.name}" wiederhergestellt`, 'success')
        }
      })
    }
  }

  // --- COLLABORATION & SHARING ACTIONS (LAZY-LOADED) ---
  let _collabService: any = null
  const getCollabService = async () => {
    if (!_collabService) {
      const { createCollaborationService } = await import('../services/collaborationService')
      _collabService = createCollaborationService({
        recipes,
        shoppingLists,
        recipeBooks,
        activeListId,
        activeBookId,
        getUid,
        confirmAction,
        showToast
      })
    }
    return _collabService
  }

  const inviteUserToRecipe = async (...args: any[]) => (await getCollabService()).inviteUserToRecipe(...args)
  const acceptRecipeInvitation = async (...args: any[]) => (await getCollabService()).acceptRecipeInvitation(...args)
  const declineRecipeInvitation = async (...args: any[]) => (await getCollabService()).declineRecipeInvitation(...args)
  const leaveRecipe = async (...args: any[]) => (await getCollabService()).leaveRecipe(...args)
  const cancelRecipeInvitation = async (...args: any[]) => (await getCollabService()).cancelRecipeInvitation(...args)
  const removeMemberFromRecipe = async (...args: any[]) => (await getCollabService()).removeMemberFromRecipe(...args)
  const createShoppingList = async (...args: any[]) => (await getCollabService()).createShoppingList(...args)
  const inviteUserToList = async (...args: any[]) => (await getCollabService()).inviteUserToList(...args)
  const acceptListInvitation = async (...args: any[]) => (await getCollabService()).acceptListInvitation(...args)
  const declineListInvitation = async (...args: any[]) => (await getCollabService()).declineListInvitation(...args)
  const revokeListInvitation = async (...args: any[]) => (await getCollabService()).revokeListInvitation(...args)
  const leaveOrDeleteShoppingList = async (...args: any[]) => {
    if (args[0]) {
      shoppingListByListIdCache.delete(args[0])
    }
    return (await getCollabService()).leaveOrDeleteShoppingList(...args)
  }
  const inviteUserToRecipeBook = async (...args: any[]) => (await getCollabService()).inviteUserToRecipeBook(...args)
  const acceptRecipeBookInvitation = async (...args: any[]) => (await getCollabService()).acceptRecipeBookInvitation(...args)
  const declineRecipeBookInvitation = async (...args: any[]) => (await getCollabService()).declineRecipeBookInvitation(...args)
  const cancelRecipeBookInvitation = async (...args: any[]) => (await getCollabService()).cancelRecipeBookInvitation(...args)
  const removeMemberFromRecipeBook = async (...args: any[]) => (await getCollabService()).removeMemberFromRecipeBook(...args)
  const leaveOrDeleteRecipeBook = async (...args: any[]) => (await getCollabService()).leaveOrDeleteRecipeBook(...args)

  const toggleFavorite = async (id: string) => {
    const item = recipes.value.find(r => r.id === id)
    if (item) {
      item.favorite = !item.favorite
      if (!isDemoMode.value) {
        await updateDoc(doc(db, 'recipes', id), { favorite: item.favorite })
      }
    }
  }

  const entityReplacement = createEntityReplacementService({
    articles,
    recipes,
    shoppingList,
    units,
    articleCategories,
    recipeCategories,
    deleteWithReplacementState,
    updateRecipe: (recipe) => updateRecipe(recipe),
    updateShoppingItem: (item) => updateShoppingItem(item),
    updateArticle: (article) => updateArticle(article),
    deleteArticle: (id, silent, customToast) => deleteArticle(id, silent, customToast),
    deleteUnit: (name) => deleteUnit(name),
    addUnit: (unit) => addUnit(unit),
    deleteArticleCategory: (id, customToast) => deleteArticleCategory(id, customToast),
    deleteRecipeCategory: (id, customToast) => deleteRecipeCategory(id, customToast),
    confirmAction,
    showToast
  })

  const {
    replaceAndDeleteArticle,
    promptDeleteArticle,
    replaceAndDeleteUnit,
    promptDeleteUnit,
    replaceAndDeleteArticleCategory,
    promptDeleteArticleCategory,
    replaceAndDeleteRecipeCategory,
    promptDeleteRecipeCategory
  } = entityReplacement

  const addToArticleHistory = async (articleId: string, amountStr: string) => {
    const { amount, unit } = parseAmountAndUnit(amountStr)
    const cleanedAmount = amount.trim()
    const cleanedUnit = unit.trim()
    if (!cleanedAmount && !cleanedUnit) return

    const article = articles.value.find(a => a.id === articleId)
    if (!article) return

    const history = article.history ? [...article.history.map(h => ({ ...h }))] : []
    
    // Check if duplicate exists and remove it
    const index = history.findIndex(h => h.amount === cleanedAmount && h.unit === cleanedUnit)
    if (index !== -1) {
      history.splice(index, 1)
    }

    // Add to the front of history
    history.unshift({ amount: cleanedAmount, unit: cleanedUnit })

    // Limit to 5 items
    const newHistory = history.slice(0, 5)

    // Save/Update article
    const updatedArticle: any = { ...article, history: newHistory }
    if (cleanedUnit) {
      updatedArticle.lastShoppingUnit = cleanedUnit
    }
    await updateArticle(updatedArticle)
  }

  const addToArticleNoteHistory = async (articleId: string, noteStr: string) => {
    const cleaned = sanitizeTextInput(noteStr, 500).trim()
    if (!cleaned) return

    const article = articles.value.find(a => a.id === articleId)
    if (!article) return

    const history = article.noteHistory ? [...article.noteHistory] : []
    const index = history.findIndex(n => n.trim().toLowerCase() === cleaned.toLowerCase())
    if (index !== -1) {
      history.splice(index, 1)
    }
    history.unshift(cleaned)
    const newHistory = history.slice(0, 10)

    await updateArticle({ ...article, noteHistory: newHistory })
  }

  const deleteArticleNoteHistoryItem = async (articleId: string, noteToRemove: string) => {
    const article = articles.value.find(a => a.id === articleId)
    if (!article || !article.noteHistory) return

    const cleaned = noteToRemove.trim().toLowerCase()
    const history = article.noteHistory.filter(n => n.trim().toLowerCase() !== cleaned)
    await updateArticle({ ...article, noteHistory: history })
  }

  const getRecentNotesForArticle = (articleId: string): string[] => {
    if (!articleId) return []
    const article = articles.value.find(a => a.id === articleId)
    const result: string[] = []
    const seen = new Set<string>()

    const add = (text?: string) => {
      if (!text) return
      const trimmed = text.trim()
      if (!trimmed) return
      const lower = trimmed.toLowerCase()
      if (!seen.has(lower)) {
        seen.add(lower)
        result.push(trimmed)
      }
    }

    // 1. Article note history (most recently saved notes)
    if (article?.noteHistory && Array.isArray(article.noteHistory)) {
      for (const n of article.noteHistory) {
        add(n)
      }
    }

    // 2. Existing notes from current shopping list items for this article
    for (const item of [...shoppingList.value].reverse()) {
      if (item.articleID === articleId && item.note) {
        add(item.note)
      }
    }

    // 3. Article default note if configured
    if (article?.note) {
      add(article.note)
    }

    return result.slice(0, 3)
  }

  // --- EINKAUFSLISTE ACTIONS ---
  const locallyDeletedIds = ref<Set<string>>(new Set())

  const syncShoppingList = () => {
    const authStore = useAuthStore()
    const uid = authStore.user?.uid
    if (!uid) return

    const targetListId = activeListId.value || uid
    if (shoppingListUnsub && currentSubscribedListId === targetListId) {
      return // Already subscribed to this list ID, do not resubscribe
    }

    if (shoppingListUnsub) {
      shoppingListUnsub()
      shoppingListUnsub = null
    }

    currentSubscribedListId = targetListId

    // If cache already has items for targetListId, immediately populate shoppingList to prevent empty state flicker
    const cached = getCachedShoppingList(targetListId)
    if (cached) {
      shoppingList.value = cached
      isShoppingListSyncing.value = false
    } else {
      isShoppingListSyncing.value = true
    }

    let ownItems: ShoppingCartItem[] = []
    let sharedItems: ShoppingCartItem[] = []

    const mergeAndUpdate = () => {
      const merged = new Map<string, ShoppingCartItem>()
      ownItems.forEach(item => {
        if (!locallyDeletedIds.value.has(item.id)) merged.set(item.id, item)
      })
      sharedItems.forEach(item => {
        if (!locallyDeletedIds.value.has(item.id)) merged.set(item.id, item)
      })
      const docs = Array.from(merged.values())
      docs.sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
      shoppingList.value = docs
      setCachedShoppingList(targetListId, docs)
      isShoppingListSyncing.value = false
      cleanupExpiredShoppingItems()
    }

    // 1. Primary listener: User's own items for target list (always authorized by ownerId rule)
    const q1 = query(
      collection(db, 'shoppingList'),
      where('ownerId', '==', uid),
      where('listId', '==', targetListId)
    )

    const unsub1 = onSnapshot(q1, (snapshot) => {
      ownItems = snapshot.docs
        .map(d => d.data() as ShoppingCartItem)
        .filter(item => item.articleID !== '__LIST__' && item.articleID !== '__INVITATION__')
      mergeAndUpdate()
    }, (err) => {
      isShoppingListSyncing.value = false
      if (err?.code === 'unauthenticated') {
        showToast('Sitzung abgelaufen – bitte neu anmelden.', 'error')
      }
    })

    // 2. Secondary listener: Items from other members if active list is shared
    const activeList = shoppingLists.value.find(l => l.id === targetListId)
    const isShared = activeList && ((activeList.sharedWith && activeList.sharedWith.length > 0) || activeList.ownerId !== uid)

    let unsub2 = () => {}
    if (isShared) {
      const q2 = query(
        collection(db, 'shoppingList'),
        where('listId', '==', targetListId)
      )
      unsub2 = onSnapshot(q2, (snapshot) => {
        sharedItems = snapshot.docs
          .map(d => d.data() as ShoppingCartItem)
          .filter(item => item.articleID !== '__LIST__' && item.articleID !== '__INVITATION__')
        mergeAndUpdate()
      }, () => {
        // Silently ignore if shared list permissions are still syncing
      })
    }

    shoppingListUnsub = () => {
      unsub1()
      unsub2()
    }

  }

  const syncRecurringPurchases = () => {
    const authStore = useAuthStore()
    const uid = authStore.user?.uid
    if (!uid) return

    if (recurringPurchasesUnsub) {
      recurringPurchasesUnsub()
      recurringPurchasesUnsub = null
    }

    const q = query(
      collection(db, 'recurring_purchases'),
      where('ownerId', '==', uid)
    )

    recurringPurchasesUnsub = onSnapshot(q, (snapshot) => {
      recurringPurchases.value = snapshot.docs.map(doc => doc.data() as RecurringPurchase)
      checkAndProcessRecurringPurchases()
    }, (err) => {
      if (err?.code === 'unauthenticated') {
        showToast('Sitzung abgelaufen – bitte neu anmelden.', 'error')
      }
    })
  }

  const selectRecipeBook = (bookId: string) => {
    if (!bookId) return
    activeBookId.value = bookId
    const authStore = useAuthStore()
    const uid = authStore.user?.uid
    if (uid && bookId !== 'demo-book') {
      safeLocalStorageSet(`activeBookId_${uid}`, bookId)
      safeLocalStorageSet('activeBookId', bookId)
      setDoc(doc(db, 'user_data', uid), {
        lastActiveBookId: bookId
      }, { merge: true }).catch(() => {})
    } else if (bookId !== 'demo-book') {
      safeLocalStorageSet('activeBookId', bookId)
    }
  }

  const selectShoppingList = (listId: string) => {
    if (!listId) return
    clearShoppingItemSelection()
    const prevListId = activeListId.value
    const cached = getCachedShoppingList(listId)
    if (cached) {
      shoppingList.value = cached
      isShoppingListSyncing.value = false
    } else if (prevListId !== listId) {
      isShoppingListSyncing.value = true
    }
    activeListId.value = listId
    const authStore = useAuthStore()
    const uid = authStore.user?.uid
    if (uid && listId !== 'demo-list') {
      safeLocalStorageSet(`activeListId_${uid}`, listId)
      safeLocalStorageSet('activeListId', listId)
      setDoc(doc(db, 'user_data', uid), {
        lastActiveListId: listId
      }, { merge: true }).catch(() => {})
    } else if (listId !== 'demo-list') {
      safeLocalStorageSet('activeListId', listId)
    }
  }

  // Watch activeListId changes to re-subscribe shoppingList and check recurring purchases
  watch(activeListId, (newId) => {
    if (newId && newId !== 'demo-list') {
      const authStore = useAuthStore()
      const uid = authStore.user?.uid
      if (uid) {
        safeLocalStorageSet(`activeListId_${uid}`, newId)
        setDoc(doc(db, 'user_data', uid), {
          lastActiveListId: newId
        }, { merge: true }).catch(() => {})
      }
      safeLocalStorageSet('activeListId', newId)
    }
    syncShoppingList()
    checkAndProcessRecurringPurchases()
  })

  // Watch activeBookId changes to persist selected recipe book across reloads / F5
  watch(activeBookId, (newId) => {
    if (newId && newId !== 'demo-book') {
      const authStore = useAuthStore()
      const uid = authStore.user?.uid
      if (uid) {
        safeLocalStorageSet(`activeBookId_${uid}`, newId)
        setDoc(doc(db, 'user_data', uid), {
          lastActiveBookId: newId
        }, { merge: true }).catch(() => {})
      }
      safeLocalStorageSet('activeBookId', newId)
    }
  })

  const reorderShoppingLists = async (lists: any[]) => {
    try {
      shoppingLists.value = lists
      const updatePromises = lists.map((l, index) => {
        const orderVal = (index + 1) * 100
        l.order = orderVal
        return updateDoc(doc(db, 'shoppingList', l.id), { order: orderVal }).catch(() => {})
      })
      await Promise.all(updatePromises)
    } catch (err) {
      console.error('Fehler beim Reorganisieren der Einkaufslisten:', err)
    }
  }

  const renameShoppingList = async (listId: string, name: string) => {
    await updateDoc(doc(db, 'shoppingList', listId), { name: sanitizeTextInput(name, 100) })
  }

  const isSameList = (listA?: string, listB?: string) => {
    if (!listA && !listB) return true
    if (listA === listB) return true
    const uid = getUid()
    if (uid) {
      const isPersonalA = !listA || listA === uid || listA === ('shop-list-default-' + uid)
      const isPersonalB = !listB || listB === uid || listB === ('shop-list-default-' + uid)
      if (isPersonalA && isPersonalB) return true
    } else {
      const isPersonalA = !listA || listA === 'shop-list-default-' || listA === 'demo-list'
      const isPersonalB = !listB || listB === 'shop-list-default-' || listB === 'demo-list'
      if (isPersonalA && isPersonalB) return true
    }
    return false
  }

  const addShoppingItem = async (articleID: string, amount: string, customNote?: string, targetListId?: string, recurringID?: string) => {
    const uid = getUid()
    const article = articles.value.find(a => a.id === articleID)
    const defaultNote = sanitizeTextInput(customNote !== undefined ? customNote : (article?.note || ''), 500)
    const sanitizedAmount = sanitizeTextInput(amount, 50)
    const id = 'shop-' + Date.now().toString() + '-' + Math.random().toString(36).substring(2, 9)

    const category = article ? articleCategories.value.find(c => c.id === article.categoryID) : null
    const categoryName = category ? category.name : 'Sonstiges'
    const categoryColor = category ? category.color : '200,200,200'

    const defaultList = !targetListId && article?.defaultListId && (
      shoppingLists.value.some(l => l.id === article.defaultListId) ||
      isSameList(article.defaultListId, 'shop-list-default-' + uid)
    ) ? article.defaultListId : null

    const resolvedListId = targetListId || defaultList || activeListId.value || ('shop-list-default-' + uid)
    const isTargetActive = (!targetListId && !defaultList) || isSameList(resolvedListId, activeListId.value)

    const newItem: any = {
      id,
      articleID,
      amount: sanitizedAmount,
      checked: false,
      note: defaultNote,
      ownerId: uid,
      listId: resolvedListId,
      articleName: sanitizeTextInput(article?.name || 'Unbekannter Artikel', 100),
      categoryName,
      categoryColor,
      articleImg: article?.img || ''
    }
    if (recurringID) {
      newItem.recurringID = recurringID
    }
    if (!recurringID && isTargetActive) {
      pendingFocusItemId.value = id  // Set BEFORE write — Firestore local cache may trigger onSnapshot synchronously
    }
    await safeFirestore(
      () => setDoc(doc(db, 'shoppingList', id), newItem),
      'Artikel konnte nicht hinzugefügt werden.'
    )
    if (isTargetActive) {
      if (!shoppingList.value.some(i => i.id === id)) {
        shoppingList.value.push(newItem)
      }
      setCachedShoppingList(resolvedListId, [...shoppingList.value])
    } else {
      listUncheckedCounts.value[resolvedListId] = (listUncheckedCounts.value[resolvedListId] || 0) + 1
      const cached = getCachedShoppingList(resolvedListId)
      if (cached && !cached.some(i => i.id === id)) {
        setCachedShoppingList(resolvedListId, [...cached, newItem])
      }
    }
    if (article) {
      const { unit: parsedUnit } = parseAmountAndUnit(amount)
      const effectiveUnit = parsedUnit.trim()
      const updatedArticle: any = {
        ...article,
        lastUsed: Date.now()
      }
      if (effectiveUnit) {
        updatedArticle.lastShoppingUnit = effectiveUnit
      }
      await updateArticle(updatedArticle)
      await addToArticleHistory(articleID, amount)
      if (defaultNote && defaultNote.trim()) {
        await addToArticleNoteHistory(articleID, defaultNote)
      }
    }
    return id
  }

  const addShoppingItemsBatch = async (
    items: Array<{ articleID: string, amount: string, customNote?: string }>,
    targetListId?: string
  ) => {
    if (!items || items.length === 0) return []
    const uid = getUid()
    const newItems: any[] = []
    const updatedArticlesMap = new Map<string, any>()
    const now = Date.now()

    const articleMap = new Map<string, any>(articles.value.map(a => [a.id, a]))
    const categoryMap = new Map<string, any>(articleCategories.value.map(c => [c.id, c]))

    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      const article = articleMap.get(item.articleID)
      const defaultNote = sanitizeTextInput(item.customNote !== undefined ? item.customNote : (article?.note || ''), 500)
      const sanitizedAmount = sanitizeTextInput(item.amount, 50)
      const id = 'shop-' + now.toString() + '-' + i + '-' + Math.random().toString(36).substring(2, 9)

      const category = article ? categoryMap.get(article.categoryID) : null
      const categoryName = category ? category.name : 'Sonstiges'
      const categoryColor = category ? category.color : '200,200,200'

      const defaultList = !targetListId && article?.defaultListId && (
        shoppingLists.value.some(l => l.id === article.defaultListId) ||
        isSameList(article.defaultListId, 'shop-list-default-' + uid)
      ) ? article.defaultListId : null

      const resolvedListId = targetListId || defaultList || activeListId.value || ('shop-list-default-' + uid)

      const newItem: any = {
        id,
        articleID: item.articleID,
        amount: sanitizedAmount,
        checked: false,
        note: defaultNote,
        ownerId: uid,
        listId: resolvedListId,
        articleName: sanitizeTextInput(article?.name || 'Unbekannter Artikel', 100),
        categoryName,
        categoryColor,
        articleImg: article?.img || ''
      }
      newItems.push(newItem)

      if (article) {
        let currentArt = updatedArticlesMap.get(article.id) || { ...article }
        const history = currentArt.history ? [...currentArt.history.map((h: any) => ({ ...h }))] : []
        const { amount: rawAmt, unit: rawUnit } = parseAmountAndUnit(item.amount)
        const cleanedAmount = rawAmt.trim()
        const cleanedUnit = rawUnit.trim()
        if (cleanedAmount || cleanedUnit) {
          const existingIdx = history.findIndex((h: any) => h.amount === cleanedAmount && h.unit === cleanedUnit)
          if (existingIdx !== -1) {
            history.splice(existingIdx, 1)
          }
          history.unshift({ amount: cleanedAmount, unit: cleanedUnit })
          currentArt.history = history.slice(0, 5)
        }
        if (cleanedUnit) {
          currentArt.lastShoppingUnit = cleanedUnit
        }
        currentArt.lastUsed = now
        updatedArticlesMap.set(article.id, currentArt)

        const artIdx = articles.value.findIndex(a => a.id === article.id)
        if (artIdx !== -1) {
          articles.value[artIdx] = { ...articles.value[artIdx], ...currentArt }
        }
      }
    }

    const firstActive = newItems.find(i => (!targetListId && !articleMap.get(i.articleID)?.defaultListId) || isSameList(i.listId, activeListId.value))
    if (firstActive) {
      pendingFocusItemId.value = firstActive.id
    }

    for (const it of newItems) {
      const art = articleMap.get(it.articleID)
      const isTargetActive = (!targetListId && !art?.defaultListId) || isSameList(it.listId, activeListId.value)
      if (isTargetActive) {
        if (!shoppingList.value.some(i => i.id === it.id)) {
          shoppingList.value.push(it)
        }
      } else {
        listUncheckedCounts.value[it.listId] = (listUncheckedCounts.value[it.listId] || 0) + 1
        const cached = getCachedShoppingList(it.listId)
        if (cached) {
          if (!cached.some(i => i.id === it.id)) {
            setCachedShoppingList(it.listId, [...cached, it])
          }
        }
      }
    }
    setCachedShoppingList(activeListId.value, [...shoppingList.value])

    await safeFirestore(async () => {
      const batch = writeBatch(db)
      for (const newItem of newItems) {
        batch.set(doc(db, 'shoppingList', newItem.id), newItem)
      }
      for (const [artId, artData] of updatedArticlesMap.entries()) {
        const updatePayload: any = { lastUsed: artData.lastUsed }
        if (artData.history) {
          updatePayload.history = artData.history
        }
        if (artData.lastShoppingUnit) {
          updatePayload.lastShoppingUnit = artData.lastShoppingUnit
        }
        batch.update(doc(db, 'articles', artId), updatePayload)
      }
      await batch.commit()
    }, 'Artikel konnten nicht hinzugefügt werden.')

    return newItems.map(i => i.id)
  }

  const updateShoppingItem = async (item: ShoppingCartItem) => {
    const sanitizedItem: ShoppingCartItem = {
      ...item,
      amount: item.amount !== undefined ? sanitizeTextInput(item.amount, 50) : item.amount,
      note: item.note !== undefined ? sanitizeTextInput(item.note, 500) : item.note,
      articleName: item.articleName !== undefined ? sanitizeTextInput(item.articleName, 100) : item.articleName
    }

    // Optimistic local update
    const index = shoppingList.value.findIndex(i => i.id === sanitizedItem.id)
    if (index !== -1) {
      shoppingList.value[index] = { ...shoppingList.value[index], ...sanitizedItem }
    }
    setCachedShoppingList(activeListId.value || currentSubscribedListId, [...shoppingList.value])

    await safeFirestore(
      () => updateDoc(doc(db, 'shoppingList', sanitizedItem.id), { ...sanitizedItem }),
      'Artikel konnte nicht aktualisiert werden.'
    )
    if (sanitizedItem.amount) {
      await addToArticleHistory(sanitizedItem.articleID, sanitizedItem.amount)
    }
    if (sanitizedItem.note && sanitizedItem.note.trim()) {
      await addToArticleNoteHistory(sanitizedItem.articleID, sanitizedItem.note)
    }
  }

  const deleteArticleHistoryItem = async (articleId: string, amount: string, unit: string) => {
    const article = articles.value.find(a => a.id === articleId)
    if (!article || !article.history) return

    const history = [...article.history.map(h => ({ ...h }))]
    const index = history.findIndex(h => 
      String(h.amount ?? '').trim() === String(amount ?? '').trim() && 
      String(h.unit ?? '').trim() === String(unit ?? '').trim()
    )
    if (index !== -1) {
      history.splice(index, 1)
      await updateArticle({ ...article, history })
    }
  }

  const deleteShoppingItem = async (id: string) => {
    const item = shoppingList.value.find(i => i.id === id)
    if (!item) return
    const itemCopy = { ...item }

    // Add to locally deleted set & optimistic local removal
    locallyDeletedIds.value.add(id)
    shoppingList.value = shoppingList.value.filter(i => i.id !== id)
    setCachedShoppingList(activeListId.value || currentSubscribedListId, [...shoppingList.value])

    if (!isDemoMode.value) {
      await safeFirestore(
        () => deleteDoc(doc(db, 'shoppingList', id)),
        'Artikel konnte nicht gelöscht werden.',
        () => {
          locallyDeletedIds.value.delete(id)
          shoppingList.value = [...shoppingList.value, itemCopy]
          setCachedShoppingList(activeListId.value || currentSubscribedListId, [...shoppingList.value])
        }
      )
    }

    const resolvedName = itemCopy.articleName || articles.value.find(a => a.id === itemCopy.articleID)?.name || 'Artikel'

    showToast(`"${resolvedName}" gelöscht`, 'success', {
      label: 'Rückgängig',
      callback: async () => {
        locallyDeletedIds.value.delete(id)
        shoppingList.value = [...shoppingList.value, itemCopy]
        setCachedShoppingList(activeListId.value || currentSubscribedListId, [...shoppingList.value])
        if (!isDemoMode.value) {
          await safeFirestore(
            () => setDoc(doc(db, 'shoppingList', itemCopy.id), itemCopy),
            'Wiederherstellen fehlgeschlagen'
          )
        }
      }
    })
  }

  const EXPIRATION_TIME_MS = 12 * 60 * 60 * 1000 // 12 hours

  const getItemCheckedAtMs = (checkedAt: unknown): number | null => {
    if (typeof checkedAt === 'number') {
      return isNaN(checkedAt) ? null : checkedAt
    }
    if (typeof checkedAt === 'string') {
      const parsed = Date.parse(checkedAt)
      return isNaN(parsed) ? null : parsed
    }
    if (checkedAt && typeof checkedAt === 'object') {
      const obj = checkedAt as { toMillis?: () => number; toDate?: () => Date; seconds?: number }
      if (typeof obj.toMillis === 'function') {
        try { return obj.toMillis() } catch { /* ignore */ }
      }
      if (typeof obj.toDate === 'function') {
        try { return obj.toDate().getTime() } catch { /* ignore */ }
      }
      if (typeof obj.seconds === 'number') {
        return obj.seconds * 1000
      }
    }
    return null
  }

  const toggleShoppingItemChecked = async (id: string) => {
    const item = shoppingList.value.find(i => i.id === id)
    if (item) {
      const newChecked = !item.checked
      const oldChecked = item.checked
      const oldCheckedAt = item.checkedAt
      const newCheckedAt = newChecked ? Date.now() : undefined

      // Optimistic local toggle
      item.checked = newChecked
      if (newChecked) {
        item.checkedAt = newCheckedAt
        const matchingRec = recurringPurchases.value.find(p => p.id === item.recurringID || p.articleID === item.articleID)
        if (matchingRec) {
          matchingRec.lastAdded = newCheckedAt
          if (!isDemoMode.value) {
            updateDoc(doc(db, 'recurring_purchases', matchingRec.id), {
              lastAdded: newCheckedAt
            }).catch(() => {})
          }
        }
      } else {
        delete item.checkedAt
      }
      setCachedShoppingList(activeListId.value || currentSubscribedListId, [...shoppingList.value])

      if (isDemoMode.value) return

      await safeFirestore(
        () => updateDoc(doc(db, 'shoppingList', id), {
          checked: newChecked,
          checkedAt: newChecked ? newCheckedAt : null
        }),
        'Status konnte nicht geändert werden.',
        () => {
          item.checked = oldChecked
          if (oldCheckedAt) {
            item.checkedAt = oldCheckedAt
          } else {
            delete item.checkedAt
          }
          setCachedShoppingList(activeListId.value || currentSubscribedListId, [...shoppingList.value])
        }
      )
    }
  }

  const cleanupExpiredShoppingItems = async () => {
    if (!autoDeleteCheckedItems12h.value) return
    const now = Date.now()

    const expiredItems = shoppingList.value.filter(item => {
      if (item.articleID === '__LIST__' || item.articleID === '__INVITATION__') return false
      if (!item.checked) return false
      const checkedAtMs = getItemCheckedAtMs(item.checkedAt)
      // Items checked without timestamp get stamped with current time so they cleanly expire in 12h
      if (checkedAtMs === null) {
        item.checkedAt = now
        return false
      }
      if (now - checkedAtMs >= EXPIRATION_TIME_MS) {
        return true
      }
      return false
    })

    if (expiredItems.length === 0) return

    // Register all expired IDs in locallyDeletedIds
    expiredItems.forEach(item => locallyDeletedIds.value.add(item.id))

    // Optimistic local clear
    const expiredIds = new Set(expiredItems.map(i => i.id))
    shoppingList.value = shoppingList.value.filter(i => !expiredIds.has(i.id))
    setCachedShoppingList(activeListId.value || currentSubscribedListId, [...shoppingList.value])

    if (isDemoMode.value) return

    try {
      const batchPromises = expiredItems.map(item => deleteDoc(doc(db, 'shoppingList', item.id)))
      await Promise.all(batchPromises)
    } catch (err) {
      console.warn('Fehler beim automatischen Löschen abgelaufener erledigter Artikel:', err)
    }
  }

  // Periodic cleanup every 60s while app is open, and on setting toggle
  if (typeof window !== 'undefined' && !import.meta.env?.TEST) {
    setInterval(() => {
      cleanupExpiredShoppingItems()
    }, 60000)
  }

  watch(autoDeleteCheckedItems12h, (newVal) => {
    if (newVal) {
      cleanupExpiredShoppingItems()
    }
  })

  const clearCheckedItems = async () => {
    const checkedItems = shoppingList.value.filter(i => i.checked)
    if (checkedItems.length === 0) return

    // Register all checked IDs in locallyDeletedIds
    checkedItems.forEach(item => locallyDeletedIds.value.add(item.id))

    // Optimistic local clear
    shoppingList.value = shoppingList.value.filter(i => !i.checked)
    setCachedShoppingList(activeListId.value || currentSubscribedListId, [...shoppingList.value])

    if (isDemoMode.value) return

    try {
      const batchPromises = checkedItems.map(item => deleteDoc(doc(db, 'shoppingList', item.id)))
      await Promise.all(batchPromises)
    } catch (err) {
      showToast('Einige Artikel konnten nicht entfernt werden.', 'error')
    }
  }

  const clearAllItems = async () => {
    if (shoppingList.value.length === 0) return

    const backup = [...shoppingList.value]
    backup.forEach(item => locallyDeletedIds.value.add(item.id))

    // Optimistic local clear
    shoppingList.value = []
    setCachedShoppingList(activeListId.value || currentSubscribedListId, [])

    if (isDemoMode.value) return

    try {
      const batchPromises = backup.map(item => deleteDoc(doc(db, 'shoppingList', item.id)))
      await Promise.all(batchPromises)
    } catch (err) {
      backup.forEach(item => locallyDeletedIds.value.delete(item.id))
      shoppingList.value = backup
      setCachedShoppingList(activeListId.value || currentSubscribedListId, backup)
      showToast('Liste konnte nicht geleert werden.', 'error')
    }
  }

  const moveShoppingItems = async (itemIds: string[], targetListId: string) => {
    if (!itemIds || itemIds.length === 0 || !targetListId) return
    const targetList = shoppingLists.value.find(l => l.id === targetListId)
    const targetListName = targetList?.name || 'andere Liste'

    const itemsToMove = shoppingList.value.filter(i => itemIds.includes(i.id))
    if (itemsToMove.length === 0) return

    const updatedItems = itemsToMove.map(item => ({
      ...item,
      listId: targetListId
    }))

    const currentActive = activeListId.value || currentSubscribedListId
    if (!isSameList(targetListId, currentActive)) {
      shoppingList.value = shoppingList.value.filter(i => !itemIds.includes(i.id))
      setCachedShoppingList(currentActive, [...shoppingList.value])

      const uncheckedMovedCount = itemsToMove.filter(i => !i.checked).length
      listUncheckedCounts.value[targetListId] = (listUncheckedCounts.value[targetListId] || 0) + uncheckedMovedCount
      if (currentActive) {
        listUncheckedCounts.value[currentActive] = Math.max(0, (listUncheckedCounts.value[currentActive] || 0) - uncheckedMovedCount)
      }

      const targetCached = getCachedShoppingList(targetListId)
      if (targetCached) {
        setCachedShoppingList(targetListId, [...targetCached, ...updatedItems])
      }
    }

    if (!isDemoMode.value) {
      await safeFirestore(async () => {
        const batch = writeBatch(db)
        for (const item of updatedItems) {
          batch.update(doc(db, 'shoppingList', item.id), { listId: targetListId })
        }
        await batch.commit()
      }, 'Artikel konnten nicht verschoben werden.')
    }

    clearShoppingItemSelection()
    const count = itemsToMove.length
    showToast(count === 1 ? `1 Artikel nach „${targetListName}“ verschoben` : `${count} Artikel nach „${targetListName}“ verschoben`, 'success')
  }

  const deleteShoppingItems = async (itemIds: string[]) => {
    if (!itemIds || itemIds.length === 0) return
    const toDelete = shoppingList.value.filter(i => itemIds.includes(i.id))
    if (toDelete.length === 0) return

    const deletedCopies = toDelete.map(i => ({ ...i }))
    toDelete.forEach(i => locallyDeletedIds.value.add(i.id))
    shoppingList.value = shoppingList.value.filter(i => !itemIds.includes(i.id))
    setCachedShoppingList(activeListId.value || currentSubscribedListId, [...shoppingList.value])

    if (!isDemoMode.value) {
      await safeFirestore(async () => {
        const batch = writeBatch(db)
        for (const item of toDelete) {
          batch.delete(doc(db, 'shoppingList', item.id))
        }
        await batch.commit()
      }, 'Artikel konnten nicht gelöscht werden.')
    }

    clearShoppingItemSelection()
    const count = toDelete.length
    const message = count === 1
      ? `"${deletedCopies[0].articleName || 'Artikel'}" gelöscht`
      : `${count} Artikel gelöscht`

    showToast(message, 'success', {
      label: 'Rückgängig',
      callback: async () => {
        deletedCopies.forEach(item => locallyDeletedIds.value.delete(item.id))
        shoppingList.value = [...shoppingList.value, ...deletedCopies]
        setCachedShoppingList(activeListId.value || currentSubscribedListId, [...shoppingList.value])
        if (!isDemoMode.value) {
          await safeFirestore(async () => {
            const batch = writeBatch(db)
            for (const item of deletedCopies) {
              batch.set(doc(db, 'shoppingList', item.id), item)
            }
            await batch.commit()
          }, 'Wiederherstellen fehlgeschlagen')
        }
      }
    })
  }

  const setShoppingItemsChecked = async (itemIds: string[], checked: boolean) => {
    if (!itemIds || itemIds.length === 0) return
    const now = Date.now()
    const toUpdate: ShoppingCartItem[] = []

    shoppingList.value.forEach(item => {
      if (itemIds.includes(item.id) && item.checked !== checked) {
        item.checked = checked
        if (checked) {
          item.checkedAt = now
        } else {
          delete item.checkedAt
        }
        toUpdate.push(item)
      }
    })

    setCachedShoppingList(activeListId.value || currentSubscribedListId, [...shoppingList.value])

    if (!isDemoMode.value && toUpdate.length > 0) {
      await safeFirestore(async () => {
        const batch = writeBatch(db)
        for (const item of toUpdate) {
          const updatePayload: any = { checked }
          if (checked) {
            updatePayload.checkedAt = now
          } else {
            updatePayload.checkedAt = null
          }
          batch.update(doc(db, 'shoppingList', item.id), updatePayload)
        }
        await batch.commit()
      }, 'Status konnte nicht aktualisiert werden.')

      if (checked) {
        for (const item of toUpdate) {
          const matchingRec = recurringPurchases.value.find(p => p.id === item.recurringID || p.articleID === item.articleID)
          if (matchingRec) {
            matchingRec.lastAdded = now
            if (!isDemoMode.value) {
              updateDoc(doc(db, 'recurring_purchases', matchingRec.id), {
                lastAdded: now
              }).catch(() => {})
            }
          }
        }
      }
    }

    clearShoppingItemSelection()
    const count = toUpdate.length
    if (count > 0) {
      showToast(checked
        ? (count === 1 ? '1 Artikel als erledigt markiert' : `${count} Artikel als erledigt markiert`)
        : (count === 1 ? '1 Artikel als offen markiert' : `${count} Artikel als offen markiert`),
        'success'
      )
    }
  }

  // --- TRASH ACTIONS ---
  const restoreFromTrash = async (trashId: string) => {
    const item = trashBin.value.find(t => t.id === trashId)
    if (item) {
      trashBin.value = trashBin.value.filter(t => t.id !== trashId)
      const { type, payload } = item
      if (isDemoMode.value) {
        if (type === 'articleCategory' && payload?.id && !articleCategories.value.some(c => c.id === payload.id)) {
          articleCategories.value.push(payload)
        } else if (type === 'article' && payload?.id && !articles.value.some(a => a.id === payload.id)) {
          articles.value.push(payload)
        } else if (type === 'recipeCategory' && payload?.id && !recipeCategories.value.some(c => c.id === payload.id)) {
          recipeCategories.value.push(payload)
        } else if (type === 'recipe' && payload?.id && !recipes.value.some(r => r.id === payload.id)) {
          recipes.value.push(payload)
        } else if (type === 'shoppingItem' && payload?.id) {
          locallyDeletedIds.value.delete(payload.id)
          const isTargetActive = !payload.listId || isSameList(payload.listId, activeListId.value)
          if (isTargetActive && !shoppingList.value.some(s => s.id === payload.id)) {
            shoppingList.value.push(payload)
          }
        }
        return
      }
      try {
        switch (type) {
          case 'articleCategory':
            if (payload?.id) {
              if (!articleCategories.value.some(c => c.id === payload.id)) {
                articleCategories.value.push(payload)
              }
              await setDoc(doc(db, 'articleCategories', payload.id), payload)
            }
            break
          case 'article':
            if (payload?.id) {
              if (!articles.value.some(a => a.id === payload.id)) {
                articles.value.push(payload)
              }
              await setDoc(doc(db, 'articles', payload.id), payload)
            }
            break
          case 'recipeCategory':
            if (payload?.id) {
              if (!recipeCategories.value.some(c => c.id === payload.id)) {
                recipeCategories.value.push(payload)
              }
              await setDoc(doc(db, 'recipeCategories', payload.id), payload)
            }
            break
          case 'recipe':
            if (payload?.id) {
              if (!recipes.value.some(r => r.id === payload.id)) {
                recipes.value.push(payload)
              }
              await setDoc(doc(db, 'recipes', payload.id), payload)
            }
            break
          case 'shoppingItem':
            if (payload?.id) {
              locallyDeletedIds.value.delete(payload.id)
              const isTargetActive = !payload.listId || isSameList(payload.listId, activeListId.value)
              if (isTargetActive && !shoppingList.value.some(s => s.id === payload.id)) {
                shoppingList.value.push(payload)
              }
              await setDoc(doc(db, 'shoppingList', payload.id), payload)
            }
            break
        }
        await deleteDoc(doc(db, 'trashBin', trashId))
      } catch (err) {
        // Safe fallback for offline / test mode
      }
    }
  }

  const deletePermanently = async (trashId: string) => {
    trashBin.value = trashBin.value.filter(t => t.id !== trashId)
    if (isDemoMode.value) return
    try {
      await deleteDoc(doc(db, 'trashBin', trashId))
    } catch (err) {
      // Safe fallback for offline / test mode
    }
  }

  const emptyTrash = async () => {
    if (isDemoMode.value) {
      trashBin.value = []
      return
    }
    const batchPromises = trashBin.value.map(t => deleteDoc(doc(db, 'trashBin', t.id)))
    await Promise.all(batchPromises)
  }

  const clearAllCloudData = async () => {
    const uid = getUid()
    const deleteFromCollection = async (colName: string) => {
      const q = query(collection(db, colName), where('ownerId', '==', uid))
      const snapshot = await getDocs(q)
      const promises = snapshot.docs.map(d => deleteDoc(d.ref))
      await Promise.all(promises)
    }
    await Promise.all([
      deleteFromCollection('articleCategories'),
      deleteFromCollection('articles'),
      deleteFromCollection('recipeCategories'),
      deleteFromCollection('recipes'),
      deleteFromCollection('shoppingList'),
      deleteFromCollection('trashBin'),
      deleteFromCollection('recipe_books'),
      deleteFromCollection('recurring_purchases'),
      deleteFromCollection('recipe_invitations'),
      deleteFromCollection('recipe_book_invitations'),
      deleteFromCollection('watch_devices')
    ])

    articleCategories.value = []
    articles.value = []
    recipeCategories.value = []
    recipes.value = []
    shoppingList.value = []
    shoppingListByListIdCache.clear()
    trashBin.value = []
    recipeBooks.value = []
    recurringPurchases.value = []
  }

  const exportData = async (options: {
    recipes: boolean
    articles: boolean
    shoppingList: boolean
    trash: boolean
    units: boolean
    settings: boolean
  }) => {
    const authStore = useAuthStore()
    const displayName = authStore.user?.displayName || authStore.user?.email || 'user'
    const { exportAppData } = await import('../services/dataBackupService')
    exportAppData(
      options,
      {
        articleCategories: articleCategories.value,
        articles: articles.value,
        recipeCategories: recipeCategories.value,
        recipes: recipes.value,
        shoppingList: shoppingList.value,
        trashBin: trashBin.value,
        units: units.value,
        activeHeaderColor: activeHeaderColor.value,
        darkMode: darkMode.value,
        themeMode: themeMode.value,
        includeCheckedInCopy: includeCheckedInCopy.value
      },
      displayName
    )
  }

  const importData = async (jsonData: any) => {
    const uid = getUid()
    const { importAppData } = await import('../services/dataBackupService')
    await importAppData(
      jsonData,
      uid,
      clearAllCloudData,
      updateUserData,
      (mode) => { themeMode.value = mode }
    )
  }

  const shareShoppingList = async () => {
    const unchecked = shoppingList.value.filter(i => !i.checked)
    const checked = shoppingList.value.filter(i => i.checked)
    if (unchecked.length === 0 && (!includeCheckedInCopy.value || checked.length === 0)) return

    const textLines = ['Mein Einkaufszettel:']
    const articleMap = new Map<string, any>(articles.value.map(a => [a.id, a]))

    if (unchecked.length > 0) {
      unchecked.forEach(item => {
        const article = articleMap.get(item.articleID)
        const name = article?.name ?? item.articleName ?? 'Unbekannter Artikel'
        const amount = item.amount ? `${item.amount} ` : ''
        textLines.push(`- ${amount}${name}`)
      })
    } else if (includeCheckedInCopy.value && checked.length > 0) {
      textLines.push('(Keine offenen Artikel)')
    }

    if (includeCheckedInCopy.value && checked.length > 0) {
      textLines.push('\nErledigt:')
      checked.forEach(item => {
        const article = articleMap.get(item.articleID)
        const name = article?.name ?? item.articleName ?? 'Unbekannter Artikel'
        const amount = item.amount ? `${item.amount} ` : ''
        textLines.push(`- ${amount}${name}`)
      })
    }

    const textToCopy = textLines.join('\n')

    if (typeof navigator !== 'undefined' && navigator.share && (!navigator.canShare || navigator.canShare({ text: textToCopy }))) {
      try {
        await navigator.share({
          title: 'Einkaufsliste',
          text: textToCopy
        })
        return
      } catch (err: any) {
        if (err && (err.name === 'AbortError' || err.code === 20)) return
        // Fallback to clipboard if share failed
      }
    }

    try {
      await navigator.clipboard.writeText(textToCopy)
      showToast('Einkaufsliste in die Zwischenablage kopiert!', 'success')
    } catch (err) {
      console.error('Fehler beim Kopieren der Einkaufsliste: ', err)
      showToast('Kopieren fehlgeschlagen.', 'error')
    }
  }

  const shareRecipe = (recipeId: string) => {
    const rec = recipes.value.find(r => r.id === recipeId)
    if (!rec) return

    const cat = recipeCategories.value.find(c => c.id === rec.categoryID)
    const catName = cat?.name ?? ''

    const textLines = [
      `Rezept: ${rec.name}`,
      catName ? `Kategorie: ${catName}` : '',
      '',
      'Zutaten:'
    ]

    const articleMap = new Map<string, any>(articles.value.map(a => [a.id, a]))
    rec.ingredients.forEach(ing => {
      const article = articleMap.get(ing.articleID)
      const name = article?.name ?? 'Unbekannter Artikel'
      const amount = ing.amount ? `${ing.amount} ` : ''
      textLines.push(`- ${amount}${name}`)
    })

    textLines.push('', 'Zubereitung:')
    rec.steps.forEach((step, index) => {
      textLines.push(`${index + 1}. ${step}`)
    })

    const textToCopy = textLines.filter(line => line !== null && line !== undefined).join('\n')

    navigator.clipboard.writeText(textToCopy)
      .then(() => {
        showToast(`Rezept "${rec.name}" in die Zwischenablage kopiert!`, 'success')
      })
      .catch(err => {
        console.error('Fehler beim Kopieren des Rezepts: ', err)
        showToast('Kopieren fehlgeschlagen.', 'error')
      })
  }

  const loadMockData = async () => {
    const uid = getUid()

    await updateUserData({ units: initialUnits })
    
    const catIdMap = new Map<string, string>()
    for (const cat of initialArticleCategories) {
      const id = 'cat-' + Date.now().toString() + '-' + Math.random().toString(36).substring(2, 9)
      catIdMap.set(cat.id, id)
      await setDoc(doc(db, 'articleCategories', id), { ...cat, id, ownerId: uid })
    }
    
    const artIdMap = new Map<string, string>()
    for (const art of initialArticles) {
      const id = 'art-' + Date.now().toString() + '-' + Math.random().toString(36).substring(2, 9)
      artIdMap.set(art.id, id)
      const mappedArt = { ...art, id, ownerId: uid, categoryID: catIdMap.get(art.categoryID) || art.categoryID }
      await setDoc(doc(db, 'articles', id), mappedArt)
    }
    
    const rcatIdMap = new Map<string, string>()
    for (const rcat of initialRecipeCategories) {
      const id = 'rcat-' + Date.now().toString() + '-' + Math.random().toString(36).substring(2, 9)
      rcatIdMap.set(rcat.id, id)
      await setDoc(doc(db, 'recipeCategories', id), { ...rcat, id, ownerId: uid })
    }
    
    for (const rec of initialRecipes) {
      const id = 'rec-' + Date.now().toString() + '-' + Math.random().toString(36).substring(2, 9)
      const mappedIngredients = rec.ingredients.map((ing: RecipeIngredient) => ({
        ...ing,
        articleID: artIdMap.get(ing.articleID) || ing.articleID
      }))
      const mappedRec = { 
        ...rec, 
        id, 
        ownerId: uid, 
        favorite: false,
        categoryID: rcatIdMap.get(rec.categoryID) || rec.categoryID,
        ingredients: mappedIngredients
      }
      await setDoc(doc(db, 'recipes', id), mappedRec)
    }
  }

  const setupStarterPack = async () => {
    isSettingUpOnboarding.value = true
    try {
      await loadMockData()
      await updateUserData({ onboardingCompleted: true })
      isOnboardingOpen.value = false
      showToast('Willkommen! Dein Starterpaket mit Artikeln & Rezepten wurde erfolgreich angelegt.', 'success')
    } catch (err) {
      console.error('Error setting up starter pack:', err)
      showToast('Fehler beim Einrichten des Starterpakets.', 'error')
    } finally {
      isSettingUpOnboarding.value = false
    }
  }

  const setupCleanStart = async () => {
    isSettingUpOnboarding.value = true
    try {
      await updateUserData({ units: initialUnits, onboardingCompleted: true })
      isOnboardingOpen.value = false
      showToast('Willkommen! Du startest mit einer leeren Sammlung.', 'success')
    } catch (err) {
      console.error('Error setting up clean start:', err)
      showToast('Fehler beim Einrichten.', 'error')
    } finally {
      isSettingUpOnboarding.value = false
    }
  }

  // --- WIEDERKEHRENDE EINKÄUFE ACTIONS ---
  const recurringService = createRecurringPurchasesService({
    recurringPurchases,
    shoppingList,
    articles,
    activeListId,
    getUid,
    addShoppingItem: (articleID, amount, note, listId, recurringID) => addShoppingItem(articleID, amount, note, listId, recurringID),
    updateShoppingItem: (item) => updateShoppingItem(item),
    showToast,
    isShoppingListSyncing,
    isSameList
  })

  const {
    addRecurringPurchase,
    updateRecurringPurchase,
    deleteRecurringPurchase,
    checkAndProcessRecurringPurchases
  } = recurringService

  // Automatically check & process recurring purchases on app open when articles & recurring purchases are loaded
  watch([articles, recurringPurchases], ([newArticles, newRecurring]) => {
    if (newArticles.length > 0 && newRecurring.length > 0) {
      checkAndProcessRecurringPurchases()
    }
  }, { immediate: true })

  // Also check when returning to app from background or next day
  if (typeof document !== 'undefined') {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAndProcessRecurringPurchases()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    unsubscribes.push(() => document.removeEventListener('visibilitychange', handleVisibilityChange))
  }



  const isMigratingThumbnails = ref(false)
  const migrationProgress = ref({ current: 0, total: 0 })

  const migrateExistingThumbnails = async () => {
    if (isMigratingThumbnails.value) return
    isMigratingThumbnails.value = true

    try {
      const { runThumbnailMigration } = await import('../services/thumbnailMigration')
      const { processed } = await runThumbnailMigration(
        {
          recipes: recipes.value,
          recipeCategories: recipeCategories.value,
          articles: articles.value,
          articleCategories: articleCategories.value
        },
        {
          updateRecipe,
          updateRecipeCategory,
          updateArticle,
          updateArticleCategory,
          onProgress: (current, total) => {
            migrationProgress.value = { current, total }
          }
        }
      )

      if (processed === 0) {
        showToast('Alle Bilder besitzen bereits optimierte Thumbnails', 'success')
      } else {
        showToast(`Thumbnails für ${processed} Bild(er) erstellt & komprimiert`, 'success')
      }
    } catch (err: any) {
      console.error('Thumbnail migration failed:', err)
      showToast('Fehler bei der Thumbnail-Generierung', 'error')
    } finally {
      isMigratingThumbnails.value = false
    }
  }

  const createRecipeBook = async (name: string) => {
    if (recipeBooks.value.length >= QUOTA_CONFIG.MAX_LISTS_PER_USER) {
      showToast(`Maximale Anzahl an Rezeptbüchern erreicht (${QUOTA_CONFIG.MAX_LISTS_PER_USER}).`, 'error')
      return null
    }
    const uid = getUid()
    const authStore = useAuthStore()
    const bookId = 'book-' + Date.now().toString() + '-' + Math.random().toString(36).substring(2, 9)
    const displayName = authStore.user?.displayName || authStore.user?.email || 'Besitzer'
    const email = authStore.user?.email?.toLowerCase() || ''
    const photoURL = authStore.userPhotoURL || authStore.user?.photoURL || ''

    const maxOrder = recipeBooks.value.reduce((max: number, b: any) => Math.max(max, typeof b.order === 'number' ? b.order : 0), 0)
    const sanitizedName = sanitizeTextInput(name, 100) || 'Neues Rezeptbuch'

    const newBook = {
      id: bookId,
      name: sanitizedName,
      ownerId: uid,
      ownerEmail: email,
      sharedWith: [],
      pendingInvites: [],
      members: [{ uid, email, displayName, photoURL }],
      order: maxOrder + 1,
      createdAt: Date.now()
    }

    recipeBooks.value = [...recipeBooks.value, newBook]
    activeBookId.value = bookId
    showToast(`Rezeptbuch "${sanitizedName}" erstellt.`, 'success')

    try {
      await setDoc(doc(db, 'recipe_books', bookId), newBook)
    } catch (err: any) {
      console.error('Error creating recipe book:', err)
      recipeBooks.value = recipeBooks.value.filter(b => b.id !== bookId)
      showToast('Fehler beim Erstellen des Rezeptbuchs.', 'error')
    }
  }

  const renameRecipeBook = async (bookId: string, newName: string) => {
    const trimmed = sanitizeTextInput(newName, 100) || 'Rezeptbuch'
    const idx = recipeBooks.value.findIndex(b => b.id === bookId)
    if (idx !== -1) {
      recipeBooks.value[idx] = { ...recipeBooks.value[idx], name: trimmed }
      recipeBooks.value = [...recipeBooks.value]
    }
    showToast('Rezeptbuch umbenannt.', 'success')

    try {
      await updateDoc(doc(db, 'recipe_books', bookId), {
        name: trimmed,
        updatedAt: Date.now()
      })
    } catch (err: any) {
      console.error('Error renaming recipe book:', err)
      showToast('Fehler beim Umbenennen des Rezeptbuchs.', 'error')
    }
  }

  const reorderRecipeBooks = async (newOrderedBooks: any[]) => {
    recipeBooks.value = newOrderedBooks
    try {
      for (let i = 0; i < newOrderedBooks.length; i++) {
        const book = newOrderedBooks[i]
        await updateDoc(doc(db, 'recipe_books', book.id), {
          order: i
        })
      }
    } catch (err: any) {
      console.error('Error reordering recipe books:', err)
    }
  }

  return {
    articleCategories,
    articles,
    recipeCategories,
    recipes,
    shoppingList,
    trashBin,
    activeHeaderColor,
    recipeColors,
    activeRecipeIdOverride,
    activeHeaderBgImage,
    activeHeaderShowBg,
    requestEditRecipe,
    requestShareRecipe,
    isGlobalSearchOpen,
    openGlobalSearch,
    closeGlobalSearch,
    darkMode,
    themeMode,
    toasts,
    showToast,
    dismissToast,
    initialize,
    waitForInitialSync,
    hasInitialSyncCompleted,
    clearStore,
    clearAllCloudData,
    loadMockData,
    addArticleCategory,
    updateArticleCategory,
    deleteArticleCategory,
    reorderArticleCategories,
    addArticle,
    updateArticle,
    deleteArticle,
    rollbackImportedArticles,
    reorderArticles,
    addRecipeCategory,
    deleteRecipeCategory,
    updateRecipeCategory,
    reorderRecipeCategories,
    addRecipe,
    updateRecipe,
    deleteRecipe,
    toggleFavorite,
    addShoppingItem,
    addShoppingItemsBatch,
    updateShoppingItem,
    deleteShoppingItem,
    toggleShoppingItemChecked,
    cleanupExpiredShoppingItems,
    clearCheckedItems,
    clearAllItems,
    moveShoppingItems,
    deleteShoppingItems,
    setShoppingItemsChecked,
    restoreFromTrash,
    deletePermanently,
    emptyTrash,
    moveToTrash,
    units,
    addUnit,
    deleteUnit,
    reorderUnits,
    renameUnit,
    updateUnit,
    exportData,
    importData,
    includeCheckedInCopy,
    updateUserData,
    shareShoppingList,
    shareRecipe,
    deleteArticleHistoryItem,
    addToArticleNoteHistory,
    deleteArticleNoteHistoryItem,
    getRecentNotesForArticle,
    pixabayApiKey,
    pexelsApiKey,
    unsplashAccessKey,
    unsplashAppId,
    unsplashSecret,
    updateImageApiKeys,
    pendingFocusItemId,
    activeEditingShoppingItemId,
    lastInlineEditClosedAt,
    suppressShoppingItemClickUntil,
    selectedShoppingItemIds,
    editingArticleItemId,
    toggleShoppingItemSelection,
    selectAllShoppingItems,
    clearShoppingItemSelection,
    openArticleEditForSelected,
    shoppingListSlim,
    shoppingListHideHeaders,
    watchTargetListId,
    watchVibrationEnabled,
    watchVoiceFeedbackEnabled,
    watchAutoListenOnLaunch,
    watchKeepScreenOnCooking,
    watchKeepScreenOnShopping,
    watchDimAmbientMode,
    compactTiles,
    quickAddToCart,
    autoDeleteCheckedItems12h,
    autoDeleteCheckedItems24h,
    startPage,
    mainSwipeMode,
    // Shared Lists Addition
    shoppingLists,
    activeListId,
    selectShoppingList,
    getCachedShoppingList,
    isShoppingListSyncing,
    pendingInvitations,
    createShoppingList,
    inviteUserToList,
    acceptListInvitation,
    declineListInvitation,
    revokeListInvitation,
    leaveOrDeleteShoppingList,
    reorderShoppingLists,
    renameShoppingList,
    // Recipe Sharing Addition
    pendingRecipeInvitations,
    inviteUserToRecipe,
    acceptRecipeInvitation,
    declineRecipeInvitation,
    leaveRecipe,
    cancelRecipeInvitation,
    removeMemberFromRecipe,
    // Recipe Books Addition
    recipeBooks,
    activeBookId,
    selectRecipeBook,
    getRecipesForBook,
    recipesForActiveBook,
    allRecipeTags,
    pendingRecipeBookInvitations,
    createRecipeBook,
    renameRecipeBook,
    reorderRecipeBooks,
    inviteUserToRecipeBook,
    acceptRecipeBookInvitation,
    declineRecipeBookInvitation,
    cancelRecipeBookInvitation,
    removeMemberFromRecipeBook,
    leaveOrDeleteRecipeBook,
    confirmDialogState,
    confirmAction,
    handleConfirmResult,
    deleteWithReplacementState,
    handleDeleteWithReplacementResult,
    promptDeleteArticle,
    replaceAndDeleteArticle,
    promptDeleteUnit,
    replaceAndDeleteUnit,
    promptDeleteArticleCategory,
    replaceAndDeleteArticleCategory,
    promptDeleteRecipeCategory,
    replaceAndDeleteRecipeCategory,
    parseAmountAndUnit,
    listUncheckedCounts,
    totalUncheckedShoppingCount,
    totalPendingInvitationsCount,
    recurringPurchases,
    desktopShoppingMode,
    addRecurringPurchase,
    updateRecurringPurchase,
    deleteRecurringPurchase,
    checkAndProcessRecurringPurchases,
    currentRecipeIdsList,
    resolvedRecipeIdsList,
    settingsAvailableTabs,
    settingsTabTrigger,
    triggerSettingsTabOffset,
    // Recipe Progress Persistence (12h)
    isStepCompleted,
    setStepCompleted,
    getCompletedStepsCount,
    getCompletedStepsSet,
    isIngredientChecked,
    toggleIngredientChecked,
    resetRecipeProgress,
    hasRecipeProgress,
    initRecipeProgressSync,
    stopRecipeProgressSync,
    keepScreenAwake,
    keepScreenAwakeUseDuration,
    invertCategoryMultiSelect,
    speechVoiceURI,
    speechRate,
    speechPitch,
    supportUrl,
    updateSupportUrl,
    isMigratingThumbnails,
    migrationProgress,
    migrateExistingThumbnails,
    // Onboarding Exports
    isOnboardingOpen,
    isSettingUpOnboarding,
    setupStarterPack,
    setupCleanStart,
    // Demo Mode Exports
    isDemoMode,
    loadDemoData,
    // Notification Center Exports
    isNotificationsModalOpen,
    notificationsHistory,
    unreadNotificationsCount,
    addNotification,
    markNotificationRead,
    markAllNotificationsRead,
    clearNotificationHistory,
    deleteNotification
  }
})
